import * as THREE from 'three';
import type { Area, Building, HeightfieldData, Road, World } from '../../shared/types.js';
import { FACADE_MATERIALS, FACADE_STYLES } from '../../shared/types.js';
import {
  bboxOfPoly,
  distToSegment2,
  pointAlong,
  pointInPoly,
  polyArea,
  polyCentroid,
  polylineLength,
  ribbon,
} from '../../shared/geom.js';
import { DEFAULT_CELL, Heightfield } from '../../shared/heightfield.js';
import { hashUnit } from '../../shared/rng.js';
import {
  KERB_HEIGHT,
  KERB_MIN_ROAD_WIDTH,
  KERB_STONE_WIDTH as KERB_TOP_WIDTH,
  ROAD_SPECS,
  shade,
} from '../../shared/osm/classify.js';
import { pavementSides } from '../../shared/osm/pavements.js';
import {
  DECK_DEPTH,
  DeckIndex,
  deckHalfWidth,
  PARAPET_HEIGHT,
  railDeckRoads,
  type DeckProfile,
  type Parapet,
} from '../../shared/decks.js';
import { buildRailways, trackBedAt, type RailwayMeshes } from './railways.js';
import { waterLevels } from '../../shared/water.js';
import { CarriagewayIndex, clearRuns, JunctionIndex } from './roadLines.js';
import { junctionArms, type JunctionNode } from './junctionArms.js';
import { buildProps, LAMP_MIN_ROAD_WIDTH, type PropMeshes } from './props.js';
import { buildSkyField } from './skyField.js';
import { applyRoadShader, SURFACE_KERB, SURFACE_PATH, SURFACE_PAVEMENT, SURFACE_TARMAC } from './road.js';
import { buildStorefronts, type Storefronts } from './storefronts.js';
import { buildRooftops, type Rooftops } from './rooftops.js';
import { buildCityObjects, type CityObjectMeshes } from './cityObjects.js';
import { regionAt } from '../../shared/region.js';
import { buildStreetLight } from './streetLight.js';
import { chunkMesh } from './chunks.js';
import { coveredWalls, uncovered } from './sharedWalls.js';
import { BAY_METRES, FLOOR_METRES, MARKING_PERIOD_METRES, makeCrossingTexture, makeLaneMarkingTexture } from './textures.js';
import {
  applyFacadeShader,
  bayMetresFor,
  facadeSeedFor,
  FACADE_FLAG_BAND,
  FACADE_FLAG_BANDED,
  type FacadeShader,
} from './facade.js';

/**
 * Builds the whole city as a handful of merged meshes plus two instanced ones.
 *
 * Merging is the single biggest win available here. A couple of square kilometres of
 * Kraków is ~2700 buildings and ~3300 road ways; drawn individually that is 6000 draw
 * calls a frame and nothing runs at 60 fps. Merged into one buffer per category with the
 * per-element colour baked into a vertex attribute, it is a handful of draw calls and the
 * GPU never notices.
 *
 * The categories are what they are because each one needs a different *material*, and a
 * material boundary is the only thing worth splitting a merge over:
 *
 * | mesh              | why it is its own draw |
 * |-------------------|------------------------|
 * | ground            | the terrain under everything, or one quad where there is none |
 * | areas             | draped fills, receives shadow, no texture |
 * | roads             | ribbons, junction discs and kerbs — one palette, no texture |
 * | road markings     | transparent decal with the lane texture |
 * | building roofs    | caps, hips and rooftop plant — no facade texture |
 * | building walls    | facade texture, plus the ground-shading patch |
 * | trees, lamps      | instanced |
 */

export interface WorldMeshes {
  group: THREE.Group;
  triangles: number;
  /** Reported so the caller can warn when a region is over budget. */
  drawCalls: number;
  /**
   * How dark it is outside, 0 for full day and 1 for full night. Driven by the weather at
   * the real place — see `weather.ts`. It turns lights on in the buildings and, further
   * down, under the street lamps: at night the city has to be somewhere people live rather
   * than a black model of one.
   */
  setNight(amount: number): void;
  /** On `low` the rooms behind the windows are not drawn — see `facade.ts`. */
  setQuality(quality: 'high' | 'low'): void;
  /** How wet the streets are, 0–1 — see `road.ts`. */
  setWet(amount: number): void;
  dispose(): void;
}

/** Warn above this; a mid-range laptop starts dropping frames somewhere past it. */
export const TRIANGLE_BUDGET = 450_000;
/** What rooftop clutter leaves unspent of the budget, for everything built after the world. */
const ROOFTOP_HEADROOM = 30_000;

const UP = new THREE.Vector3(0, 1, 0);

/**
 * Side of the square tiles the heavy meshes are cut into. Smaller culls more finely and costs
 * more draw calls; see DECISIONS.md §92 for how this was settled.
 */
export const TILE_METRES = 400;

// --- kerbs -----------------------------------------------------------------
const KERB_TOP_COLOR = 0xada79c;
const KERB_FACE_COLOR = 0x928c82;
const KERB_SIDE_COLOR = 0x7d786f;

/**
 * The pavement is sized in `classify.ts` rather than here, because the physics has to agree
 * with it — see `pavementWidthFor` there for why the width scales with the street.
 *
 * A cooler, more neutral grey than the kerb stone beside it (`KERB_TOP_COLOR` leans warm,
 * R over B by 17) and lighter than any tarmac in `ROAD_SPECS` — concrete rather than either
 * of its neighbours, not a third shade of the same stone.
 */
const PAVEMENT_COLOR = 0x999c9a;



/**
 * Roads at least this wide get painted lanes; below it a centre line is a fiction, and an
 * old town's 5 m service alleys would end up looking like dual carriageways.
 *
 * 8 m is the residential class, which is also the width at which the markings start doing
 * navigational work: from the air the paint is the clearest signal of where the drivable
 * network actually goes.
 */
const MARKING_MIN_ROAD_WIDTH = 8;

/**
 * Vertex tint under the marking texture. Sunlight here is worth about 1.5×, so painting the
 * decal white would clip it flat; starting below white leaves the paint somewhere to go.
 */
const MARKING_COLOR = 0xbdb9b0;

export function buildWorldMeshes(world: World): WorldMeshes {
  const group = new THREE.Group();
  group.name = 'world';
  const disposables: { dispose(): void }[] = [];
  let triangles = 0;

  const junctions = new JunctionIndex(world.junctions);

  /**
   * The terrain, and the only thing that decides whether any of the draping below happens.
   *
   * `isFlat` is not just an optimisation: it is the guarantee that a world with no elevation
   * data emits exactly the geometry it emitted before there was any such thing as elevation,
   * which is what the offline fixtures and the whole existing test suite are pinned to.
   */
  const terrain = world.ground ? Heightfield.from(world.ground) : Heightfield.flat();

  // --- ground --------------------------------------------------------------
  const groundSize = Math.max(world.extent.x, world.extent.z) * 2 + 2400;
  const drawnGround =
    terrain.isFlat || !world.ground
      ? null
      : displacedGroundGeometry(terrain, world.ground, groundSize / 2);
  const groundGeo = drawnGround ? drawnGround.geometry : flatGroundGeometry(groundSize);
  const groundMat = new THREE.MeshStandardMaterial({ color: 0x2f3a2c, roughness: 0.95 });
  const ground = new THREE.Mesh(groundGeo, groundMat);
  ground.name = 'ground';
  ground.renderOrder = -10;
  ground.receiveShadow = true;
  group.add(ground);
  disposables.push(groundGeo, groundMat);
  triangles += (groundGeo.getIndex()?.count ?? groundGeo.getAttribute('position').count) / 3;

  /**
   * The heavy merged meshes — walls, roofs, roads — go in as tiles of the ground, so culling
   * can work on them; see `chunks.ts`. The rest are left whole. The areas' coplanar polygons
   * rely on the order they were added in to settle which one shows, and tiles would reorder
   * them; the two decals are a few thousand transparent triangles that cast nothing, and
   * tiling them would be draw calls spent saving nothing.
   */
  const addTiled = (built: { mesh: THREE.Mesh; geometry: THREE.BufferGeometry }): void => {
    const tiled = chunkMesh(built.mesh, TILE_METRES);
    group.add(tiled.group);
    disposables.push(...tiled.geometries);
    built.geometry.dispose();
  };

  // --- areas ---------------------------------------------------------------
  const levels = waterLevels(world.areas, terrain);
  const areaBuilder = new MeshBuilder();
  // Larger polygons first so a park drawn on top of a landuse blanket wins the depth
  // test at equal-ish heights.
  const sortedAreas = [...world.areas].sort((a, b) => a.y - b.y || polySpan(b.poly) - polySpan(a.poly));
  for (const area of sortedAreas) addAreaPolygon(areaBuilder, area, terrain, drawnGround, levels);
  const areaMesh = areaBuilder.toMesh('areas', { flatShading: false, roughness: 0.92 });
  if (areaMesh) {
    areaMesh.mesh.receiveShadow = true;
    group.add(areaMesh.mesh);
    disposables.push(areaMesh.geometry, areaMesh.material);
    triangles += areaMesh.triangles;
  }

  // --- roads (ribbons + junction discs + kerbs) ----------------------------
  const deckIndex = DeckIndex.build(world, terrain);
  const decks = deckIndex.profileMap();
  const lifts = deckIndex.liftMap();
  const surfaceOf = (road: Road, x: number, z: number): number => {
    const deck = decks.get(road.id);
    if (deck) return deck(x, z);
    return terrain.heightAt(x, z) + (lifts.get(road.id)?.(x, z) ?? 0);
  };
  // Keeps every street's kerb, pavement and paint off every other street's tarmac — see
  // `CarriagewayIndex`. Built once up here rather than per road: it indexes every road in
  // the world regardless of which one is asking.
  const carriageways = new CarriagewayIndex(world.roads, surfaceOf);
  const roadBuilder = new MeshBuilder({ surface: true });
  const discSurface = junctionSurfaces(world, decks, lifts, surfaceOf);
  for (const junction of world.junctions) {
    addDisc(
      roadBuilder,
      junction.x,
      junction.z,
      junction.y,
      junction.r,
      junction.color,
      12,
      terrain,
      discSurface.get(`${junction.x}|${junction.z}`),
    );
  }
  for (const road of world.roads) {
    const spec = ROAD_SPECS[road.cls];
    const line = drapeLine(road.pts, terrain, decks.get(road.id) ?? null, lifts.get(road.id) ?? null);
    const mesh = ribbon(line.pts, road.width, road.y);
    drapeRibbon(mesh.positions, line.h);
    roadBuilder.surface = spec.drivable ? SURFACE_TARMAC : SURFACE_PATH;
    roadBuilder.addIndexed(mesh.positions, mesh.indices, spec.color, line.h ? undefined : UP);
  }
  roadBuilder.surface = SURFACE_TARMAC;
  for (const road of world.roads) {
    addKerbs(roadBuilder, road, terrain, decks, lifts, carriageways, surfaceOf);
  }
  const roadMesh = roadBuilder.toMesh('roads', { flatShading: false, roughness: 0.86 });
  const roadShader = roadMesh ? applyRoadShader(roadMesh.material as THREE.MeshStandardMaterial) : null;
  if (roadMesh) {
    roadMesh.mesh.receiveShadow = true;
    addTiled(roadMesh);
    disposables.push(roadMesh.material);
    triangles += roadMesh.triangles;
  }

  // --- lane markings -------------------------------------------------------
  const markingBuilder = new MeshBuilder({ uv: true });
  for (const road of world.roads) {
    addLaneMarkings(markingBuilder, road, junctions, terrain, decks, lifts, carriageways, surfaceOf);
  }
  const markingTexture = makeLaneMarkingTexture();
  const markingMesh = markingBuilder.toMesh('road-markings', {
    flatShading: false,
    roughness: 0.7,
    map: markingTexture,
    transparent: true,
  });
  if (markingMesh) {
    markingMesh.mesh.receiveShadow = true;
    markingMesh.mesh.renderOrder = 1;
    group.add(markingMesh.mesh);
    disposables.push(markingMesh.geometry, markingMesh.material, markingTexture);
    triangles += markingMesh.triangles;
  } else {
    markingTexture.dispose();
  }

  // --- crossings -----------------------------------------------------------
  //
  // Its own mesh rather than more triangles in the lane-marking one, because the two decals
  // disagree about what `v` means: lane paint repeats every `MARKING_PERIOD_METRES` along the
  // street, and a crossing happens exactly once. One extra draw call for the whole city.
  const arms = junctionArms(world);
  const crossingBuilder = new MeshBuilder({ uv: true });
  addCrossings(crossingBuilder, arms, terrain, decks, lifts, carriageways, surfaceOf);
  const crossingTexture = makeCrossingTexture();
  const crossingMesh = crossingBuilder.toMesh('road-crossings', {
    flatShading: false,
    roughness: 0.7,
    map: crossingTexture,
    transparent: true,
  });
  if (crossingMesh) {
    crossingMesh.mesh.receiveShadow = true;
    crossingMesh.mesh.renderOrder = 2;
    group.add(crossingMesh.mesh);
    disposables.push(crossingMesh.geometry, crossingMesh.material, crossingTexture);
    triangles += crossingMesh.triangles;
  } else {
    crossingTexture.dispose();
  }

  // --- railways ------------------------------------------------------------
  let railways: RailwayMeshes | null = null;
  try {
    railways = buildRailways(world, terrain, deckIndex);
    for (const object of railways?.objects ?? []) group.add(object);
    triangles += railways?.triangles ?? 0;
  } catch (error) {
    console.warn('[worldMesh] could not build the railway', error);
    railways = null;
  }

  // --- buildings -----------------------------------------------------------
  const roofBuilder = new MeshBuilder();
  const wallBuilder = new MeshBuilder({ uv: true, facade: true });
  // What holds the bridges up goes in with the roofs: untextured, flat-shaded concrete, and
  // — the reason it is here rather than with the roads — casting a shadow. A viaduct that
  // leaves no shadow on the street under it does not look like it is over anything.
  // Railway bridges are decks like any other (see `railDeckRoads`), and are held up the same way.
  const structures = [...world.roads, ...railDeckRoads(world)];
  const onTrack = trackBedAt(world);
  for (const road of structures) {
    addElevatedStructure(roofBuilder, road, terrain, decks, lifts, carriageways, onTrack);
  }
  const roadsById = new Map(structures.map((road) => [road.id, road]));
  for (const parapet of deckIndex.parapets) {
    const road = roadsById.get(parapet.wayId);
    if (road) addParapet(roofBuilder, parapet, road);
  }
  // The *lowest* ground under the footprint, never the centroid: a building set at its
  // centroid height on any slope has one corner in the air, and half a hillside of them
  // reads as a city that has come unstuck. Setting it at the minimum buries the uphill
  // corner instead, which is what a real building on a slope does.
  const buildingLifts = world.buildings.map((b) => lowestGroundUnder(b.poly, terrain));
  const shared = coveredWalls(
    world.buildings,
    world.buildings.map((b, i) => ({ bottom: b.minHeight + buildingLifts[i], top: b.height + buildingLifts[i] })),
    (x, z) => terrain.heightAt(x, z),
  );
  world.buildings.forEach((building, i) => {
    addBuilding(roofBuilder, wallBuilder, building, world.seed, buildingLifts[i], shared.get(i));
  });

  const roofMesh = roofBuilder.toMesh('building-roofs', { flatShading: true, roughness: 0.82 });
  if (roofMesh) {
    roofMesh.mesh.castShadow = true;
    roofMesh.mesh.receiveShadow = true;
    addTiled(roofMesh);
    disposables.push(roofMesh.material);
    triangles += roofMesh.triangles;
  }

  const wallMesh = wallBuilder.toMesh('building-walls', { flatShading: true, roughness: 0.85 });
  let facade: FacadeShader | null = null;
  if (wallMesh) {
    facade = applyFacadeShader(wallMesh.material as THREE.MeshStandardMaterial);
    wallMesh.mesh.castShadow = true;
    wallMesh.mesh.receiveShadow = true;
    addTiled(wallMesh);
    disposables.push(wallMesh.material);
    triangles += wallMesh.triangles;
  }

  // --- trees and street lamps ----------------------------------------------
  let props: PropMeshes | null = null;
  try {
    props = buildProps(world, junctions, terrain, {}, arms, surfaceOf);
    for (const object of props.objects) group.add(object);
    triangles += props.triangles;
  } catch (error) {
    // Scenery is not worth losing a race over.
    console.warn('[worldMesh] could not build scenery', error);
    props = null;
  }

  // --- canopies, stores, pitches, car parks, café tables --------------------
  //
  // Before the shop signs, which paint these names too, and before the rooftops, which
  // spend whatever is left: a café's tables are in the chase camera's frame, a chimney
  // mostly is not. See `cityObjects.ts`.
  let cityObjects: CityObjectMeshes | null = null;
  try {
    cityObjects = buildCityObjects(world, terrain);
  } catch (error) {
    console.warn('[worldMesh] could not build city objects', error);
    cityObjects = null;
  }

  // --- shop signs and awnings ----------------------------------------------
  let storefronts: Storefronts | null = null;
  try {
    storefronts = buildStorefronts(
      world,
      (b) => b.minHeight + lowestGroundUnder(b.poly, terrain),
      cityObjects?.boards ?? [],
    );
    for (const object of storefronts.objects) group.add(object);
    triangles += storefronts.triangles;
  } catch (error) {
    // A sign is not worth losing a race over either.
    console.warn('[worldMesh] could not build storefronts', error);
    storefronts = null;
  }
  try {
    cityObjects?.furnish(TRIANGLE_BUDGET - ROOFTOP_HEADROOM - triangles - (cityObjects?.triangles ?? 0));
    for (const object of cityObjects?.objects ?? []) group.add(object);
    triangles += cityObjects?.triangles ?? 0;
  } catch (error) {
    console.warn('[worldMesh] could not furnish the city', error);
    cityObjects = null;
  }

  // --- what each surface can see of the sky ----------------------------------
  //
  // Before the street lights, which add at a later point in the shader and must not be
  // dimmed by it — see `skyField.ts`. Roofs are left out: a roof sees the whole sky.
  const skyField = buildSkyField(world);
  disposables.push(skyField);
  for (const material of [
    groundMat,
    areaMesh?.material,
    roadMesh?.material,
    markingMesh?.material,
    crossingMesh?.material,
    railways?.material,
  ]) {
    if (material) skyField.patch(material, 'ground');
  }
  if (wallMesh) skyField.patch(wallMesh.material, 'wall');

  // --- chimneys and water towers --------------------------------------------
  //
  // Last of the geometry, because it spends whatever the triangle budget has left — with a
  // margin for the route furniture and the cars, which are not counted here.
  let rooftops: Rooftops | null = null;
  try {
    rooftops = buildRooftops(
      world,
      regionAt(world.origin.lat, world.origin.lon),
      (b) => b.minHeight + lowestGroundUnder(b.poly, terrain),
      TRIANGLE_BUDGET - ROOFTOP_HEADROOM - triangles,
    );
    for (const object of rooftops.objects) group.add(object);
    triangles += rooftops.triangles;
  } catch (error) {
    console.warn('[worldMesh] could not build rooftops', error);
    rooftops = null;
  }

  // --- street lighting -----------------------------------------------------
  //
  // Patched into everything at street level, after every material exists. Roofs too: they
  // are well above the light's reach and get nothing from it, but a single-storey kiosk's
  // roof is not, and leaving it out would leave one dark box in a lit street. Without
  // scenery there are no posts, so every street gets lanterns instead.
  const streetLight = buildStreetLight(
    world,
    [...(props?.lampHeads ?? []), ...(cityObjects?.lights ?? [])],
    props ? LAMP_MIN_ROAD_WIDTH : Infinity,
  );
  disposables.push(streetLight);
  for (const material of [
    groundMat,
    areaMesh?.material,
    markingMesh?.material,
    crossingMesh?.material,
    roofMesh?.material,
    railways?.material,
    ...(cityObjects?.objects.map((o) => (o as THREE.Mesh).material as THREE.Material) ?? []),
  ]) {
    if (material) streetLight.patch(material);
  }
  if (wallMesh) streetLight.patch(wallMesh.material, { wall: true });
  for (const material of props?.litMaterials ?? []) streetLight.patch(material, { instanced: true });
  // The road also reflects the lamps — see `streetLight.ts`.
  if (roadMesh) streetLight.patch(roadMesh.material, { reflect: true });

  // Every mesh, not every child: the tiled meshes are a group of draw calls each.
  let drawCalls = 0;
  group.traverse((object) => {
    if ((object as THREE.Mesh).isMesh) drawCalls++;
  });

  return {
    group,
    triangles,
    drawCalls,
    setNight(amount: number) {
      const night = amount < 0 ? 0 : amount > 1 ? 1 : amount;
      // Not 1.0 at the top: the emissive term is added straight on after lighting, and a
      // window at full strength blows out to a flat white rectangle with no glass in it.
      facade?.setNight(night);
      storefronts?.setNight(night);
      cityObjects?.setNight(night);
      streetLight.setNight(night);
      // Optional-chained through a cast: street lamps light up here too, and this file and
      // `props.ts` were written in parallel.
      (props as { setNight?(n: number): void } | null)?.setNight?.(night);
    },
    setQuality(quality) {
      facade?.setQuality(quality);
    },
    setWet(amount) {
      roadShader?.setWet(amount);
      railways?.setWet(amount);
      streetLight.setWet(amount);
    },
    dispose() {
      props?.dispose();
      storefronts?.dispose();
      cityObjects?.dispose();
      rooftops?.dispose();
      railways?.dispose();
      for (const d of disposables) d.dispose();
      group.clear();
    },
  };
}

// ---------------------------------------------------------------------------
// geometry helpers
// ---------------------------------------------------------------------------

function polySpan(poly: number[]): number {
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (let i = 0; i < poly.length; i += 2) {
    if (poly[i] < minX) minX = poly[i];
    if (poly[i] > maxX) maxX = poly[i];
    if (poly[i + 1] < minZ) minZ = poly[i + 1];
    if (poly[i + 1] > maxZ) maxZ = poly[i + 1];
  }
  return (maxX - minX) * (maxZ - minZ);
}

/** One quad under everything, which is what a world with no elevation data gets. */
function flatGroundGeometry(size: number): THREE.BufferGeometry {
  const geometry = new THREE.PlaneGeometry(size, size);
  geometry.rotateX(-Math.PI / 2);
  return geometry;
}

/**
 * The ground as a displaced grid over the heightfield's footprint, with a skirt out to the
 * horizon.
 *
 * Two decisions worth spelling out.
 *
 * The step is a multiple of the field's own cell rather than a fixed distance, because the
 * field has already been smoothed with a Gaussian of about 11 m: below roughly a 40 m
 * wavelength there is nothing left in it to sample, so a grid finer than half that is
 * spending triangles on detail the generator deliberately removed. `GROUND_MAX_SEGMENTS` is
 * the backstop for a large region, where the alternative is a five-figure triangle count for
 * ground that is mostly hidden under roads, parks and buildings anyway.
 *
 * The outer ring of vertices is pushed out to the horizon rather than a separate flat plane
 * being drawn around the field. `heightAt` clamps outside the grid, so that ring already
 * carries exactly the height of the field edge beside it — the join is seamless for free,
 * where a flat apron at Y = 0 would put a cliff around the world wherever the DEM's margin
 * happened not to be at datum.
 */
const GROUND_CELLS_PER_QUAD = 2;
const GROUND_MAX_SEGMENTS = 128;

/**
 * The ground as it is actually drawn, and a way to ask what height it has anywhere.
 *
 * The sampler is the important half. Everything laid on the ground is a second, independent
 * triangulation of the same curved field, and two such approximations disagree by far more
 * than the centimetres `AREA_Y` allows between them — which is how the Seine ended up
 * scattered with ground-coloured debris. Handing the fills the *drawn* surface rather than
 * the field lets them be refined until they agree with the thing they can actually lose a
 * depth test against, instead of with an ideal surface neither mesh draws.
 */
interface DrawnGround {
  geometry: THREE.BufferGeometry;
  heightAt(x: number, z: number): number;
}

function displacedGroundGeometry(
  terrain: Heightfield,
  data: HeightfieldData,
  far: number,
): DrawnGround {
  const spanX = (data.cols - 1) * data.cell;
  const spanZ = (data.rows - 1) * data.cell;
  const segsX = gridSegments(spanX, data.cell);
  const segsZ = gridSegments(spanZ, data.cell);

  const coord = (i: number, segs: number, origin: number, span: number, edge: number): number => {
    if (i === 0) return -edge;
    if (i === segs + 2) return edge;
    return origin + ((i - 1) / segs) * span;
  };

  const cols = segsX + 3;
  const rows = segsZ + 3;
  const positions = new Float32Array(cols * rows * 3);
  const sampled = new Float64Array(cols * rows);
  for (let r = 0; r < rows; r++) {
    const z = coord(r, segsZ, data.originZ, spanZ, far);
    for (let c = 0; c < cols; c++) {
      const x = coord(c, segsX, data.originX, spanX, far);
      const o = (r * cols + c) * 3;
      positions[o] = x;
      positions[o + 2] = z;
      sampled[r * cols + c] = terrain.heightAt(x, z);
    }
  }

  /*
   * Interior vertices are pulled down by the local concavity before being written out.
   *
   * A grid interpolates straight lines between its samples, so across a hollow the drawn
   * surface rides *above* the field it is approximating — in the Marais, by up to 1.15 m.
   * Everything else in the world is drawn a few centimetres above that same field, so
   * wherever that happens the ground swallows the road, the paint and the park on top of it.
   *
   * Subtracting the discrete Laplacian where it is positive costs no triangles, is exactly
   * zero on any planar slope (so a hillside is not dragged down), and takes the worst
   * overshoot to 0.10 m — under the height a road is already drawn at. The price is that the
   * bottom of a hollow is dug up to a metre deeper than it should be, which is invisible:
   * nothing is under the ground.
   */
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const i = r * cols + c;
      const h = sampled[i];
      if (r > 0 && r < rows - 1 && c > 0 && c < cols - 1) {
        const around =
          (sampled[i - 1] + sampled[i + 1] + sampled[i - cols] + sampled[i + cols]) / 4;
        positions[i * 3 + 1] = h - Math.max(0, around - h);
      } else {
        positions[i * 3 + 1] = h;
      }
    }
  }

  const indices = new Uint32Array((cols - 1) * (rows - 1) * 6);
  let k = 0;
  for (let r = 0; r < rows - 1; r++) {
    for (let c = 0; c < cols - 1; c++) {
      const a = r * cols + c;
      const b = a + 1;
      const d = a + cols;
      const e = d + 1;
      // Counter-clockwise seen from +Y, so the ground faces the sky.
      indices[k++] = a;
      indices[k++] = d;
      indices[k++] = b;
      indices[k++] = b;
      indices[k++] = d;
      indices[k++] = e;
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();

  // Bilinear over the same corrected heights the mesh was built from, clamped to the field:
  // outside it the skirt is a stretch of the edge value, which is what clamping returns.
  const stepX = spanX / segsX;
  const stepZ = spanZ / segsZ;
  const heightOf = (r: number, c: number) => positions[(r * cols + c) * 3 + 1];
  return {
    geometry,
    heightAt(x: number, z: number): number {
      const fx = Math.min(segsX - 1e-9, Math.max(0, (x - data.originX) / stepX));
      const fz = Math.min(segsZ - 1e-9, Math.max(0, (z - data.originZ) / stepZ));
      const c = Math.min(segsX - 1, Math.floor(fx)) + 1;
      const r = Math.min(segsZ - 1, Math.floor(fz)) + 1;
      const tx = fx - (c - 1);
      const tz = fz - (r - 1);
      const top = heightOf(r, c) + (heightOf(r, c + 1) - heightOf(r, c)) * tx;
      const bot = heightOf(r + 1, c) + (heightOf(r + 1, c + 1) - heightOf(r + 1, c)) * tx;
      return top + (bot - top) * tz;
    },
  };
}

function gridSegments(span: number, cell: number): number {
  return Math.max(1, Math.min(GROUND_MAX_SEGMENTS, Math.ceil(span / (cell * GROUND_CELLS_PER_QUAD))));
}

function toVec2Ring(poly: number[]): THREE.Vector2[] {
  const out: THREE.Vector2[] = [];
  for (let i = 0; i < poly.length; i += 2) out.push(new THREE.Vector2(poly[i], poly[i + 1]));
  return out;
}

/**
 * Triangulates a ring (with holes) that lies in the XZ plane at height `y`.
 *
 * Note the reversed winding: `triangulateShape` works in 2D (x, y) and we map that to
 * (x, z), which flips handedness — emitting the triangles as returned would give every
 * park and every roof a downward-facing normal.
 */
function addFlatPolygon(
  builder: MeshBuilder,
  poly: number[],
  holes: number[][],
  y: number,
  color: number,
): void {
  const shape = triangulateRings(poly, holes);
  if (!shape) return;
  const positions: number[] = [];
  for (const v of shape.verts) positions.push(v.x, y, v.y);
  const indices: number[] = [];
  for (const f of shape.faces) indices.push(f[2], f[1], f[0]);
  builder.addIndexed(positions, indices, color, UP);
}

function triangulateRings(
  poly: number[],
  holes: number[][],
): { verts: THREE.Vector2[]; faces: number[][] } | null {
  if (poly.length < 6) return null;
  const contour = toVec2Ring(poly);
  const holeRings = holes.filter((h) => h.length >= 6).map(toVec2Ring);
  try {
    const faces = THREE.ShapeUtils.triangulateShape(contour, holeRings);
    return { verts: contour.concat(...holeRings), faces };
  } catch {
    // Self-intersecting OSM footprints do exist; skipping one is better than throwing
    // away the whole city.
    return null;
  }
}

/**
 * How far anything drawn on the ground is allowed to sit from the ground, in metres, and the
 * chord lengths that bracket the test.
 *
 * Draping is refined against the *measured* error rather than to a fixed step, and the
 * difference is most of the terrain budget. A fixed 20 m step spends the same triangles on
 * Manhattan — 18 m of relief across the whole region, where four corners describe a park
 * perfectly well — as on the Marais dropping to the Seine, and it took Paris 40,000
 * triangles past budget to do it. Splitting only where a chord actually departs from the
 * field puts the triangles on the slopes and nowhere else.
 *
 * The tolerance is a third of a metre because that is roughly a kerb height: below it the
 * error is hidden by the things standing on the ground, and above it a park visibly floats.
 *
 * The two chord limits are the guard rails around a criterion that can be fooled. `MAX`
 * forces a split regardless, because a long chord across a symmetric valley can have its
 * midpoint exactly on the line and still be forty metres out in between. `MIN` stops the
 * recursion at the field's own cell size, below which there is nothing left to resolve.
 */
const DRAPE_TOLERANCE = 0.33;
const DRAPE_MIN_CHORD = DEFAULT_CELL;
const DRAPE_MAX_CHORD = 48;

/** A guard, not a target: 4096 sub-triangles from one source triangle is already absurd. */
const REFINE_MAX_DEPTH = 12;

/**
 * Whether a straight line from a to b needs a vertex in the middle of it to follow the
 * ground. A pure function of the two endpoints and the field, which is what keeps the area
 * refinement free of T-junctions: two triangles sharing an edge always answer this the same.
 */
function chordNeedsSplit(
  ax: number,
  az: number,
  bx: number,
  bz: number,
  terrain: Heightfield,
): boolean {
  const len2 = (ax - bx) * (ax - bx) + (az - bz) * (az - bz);
  if (len2 <= DRAPE_MIN_CHORD * DRAPE_MIN_CHORD) return false;
  if (len2 > DRAPE_MAX_CHORD * DRAPE_MAX_CHORD) return true;
  const mid = terrain.heightAt((ax + bx) / 2, (az + bz) / 2);
  const chord = (terrain.heightAt(ax, az) + terrain.heightAt(bx, bz)) / 2;
  return Math.abs(mid - chord) > DRAPE_TOLERANCE;
}

/**
 * Whether the straight line a fill would draw between two of its vertices passes under the
 * ground mesh.
 *
 * This is the test that keeps a park or a river out of the ground rather than merely close
 * to it. The refinement above asks whether a chord follows the *field*; this asks whether it
 * loses to the surface actually drawn underneath, which is a different and coarser
 * triangulation of the same curve. `AREA_Y` gives water five centimetres and `urban` two —
 * a ladder designed to separate flat coplanar polygons — and two independent triangulations
 * of a curved surface disagree by far more than that wherever the ground bends between their
 * samples. Splitting there makes the two agree instead of trying to hold them apart.
 *
 * Like the chord test, it depends only on the edge's own endpoints, so two triangles sharing
 * an edge always answer it the same way and the fill stays free of T-junctions.
 */
function chordDipsIntoGround(
  ax: number,
  az: number,
  bx: number,
  bz: number,
  y: number,
  terrain: Heightfield,
  ground: DrawnGround,
): boolean {
  const len2 = (ax - bx) * (ax - bx) + (az - bz) * (az - bz);
  if (len2 <= DRAPE_MIN_CHORD * DRAPE_MIN_CHORD) return false;
  const mx = (ax + bx) / 2;
  const mz = (az + bz) / 2;
  const chord = (terrain.heightAt(ax, az) + terrain.heightAt(bx, bz)) / 2 + y;
  return chord < ground.heightAt(mx, mz) + GROUND_CLEARANCE;
}

/** What a fill has to keep between itself and the ground mesh to survive the depth test. */
const GROUND_CLEARANCE = 0.02;

/**
 * The same fill, refined and dropped onto the terrain.
 *
 * The refinement is repeated bisection of the longest edge at its midpoint, which is chosen
 * over a subdivision grid or a 1→4 split for one reason: whether an edge is split depends
 * only on that edge's own length, and the split point is its midpoint, so two triangles
 * sharing an edge always make the same decision about it. That means no T-junctions and no
 * hairline cracks along the seams inside a park, without either side needing to know its
 * neighbour exists.
 */
function addDrapedPolygon(
  builder: MeshBuilder,
  poly: number[],
  holes: number[][],
  y: number,
  color: number,
  terrain: Heightfield,
  ground: DrawnGround,
  maxLift: number,
): void {
  const shape = triangulateRings(poly, holes);
  if (!shape) return;
  const out: number[] = [];
  const v = shape.verts;
  for (const f of shape.faces) {
    const a = v[f[2]];
    const b = v[f[1]];
    const c = v[f[0]];
    refineTriangle(out, a.x, a.y, b.x, b.y, c.x, c.y, y, terrain, ground, REFINE_MAX_DEPTH);
  }
  liftClearOfGround(out, ground, maxLift);
  const indices = new Array<number>(out.length / 3);
  for (let i = 0; i < indices.length; i++) indices[i] = i;
  // No shared normal: the whole point is that this surface is no longer flat.
  builder.addIndexed(out, indices, color);
}

/**
 * Raises a whole fill until none of it is under the ground it is drawn on.
 *
 * The refinement above splits any *chord* that would pass under the ground mesh, but the sag
 * in the middle of a triangle is not a chord: three edges can each clear the ground while the
 * face between them does not. This is the backstop for that, and for anything that runs out
 * of subdivision depth. Without either, the Marais came up with the Seine full of
 * ground-coloured holes — from the air it read as the river being torn open, and nothing in
 * the type checker or the test suite saw it.
 *
 * The lift is per polygon and measured from that polygon's own error, so it is zero on level
 * ground and only as large as the terrain under that polygon demands. It is uniform across
 * the polygon, so it cannot open a seam inside one.
 *
 * The ceiling is what stops the cure being worse than the disease. The lowest road is drawn
 * at 0.14 m and the highest fill (`paved`) already sits at 0.10, so a fill has only what is
 * left under the tarmac to rise into — and one bad triangle would otherwise take a whole
 * `landuse` blanket over half the street network with it. Measured on the Marais, an
 * uncapped lift put 93% of the area mesh above road height.
 *
 * Water used to be the exception here, allowed a metre and a half of lift because it is the
 * largest and flattest polygon in the city and sagged worst. It is no longer draped at all —
 * see `waterLevels` in `shared/water.ts` — so nothing needs that exemption any more, and the
 * ceiling below is the only one there is.
 */
/** Just under `ROAD_BASE_Y`, so a fill can never come up through the carriageway. */
const FILL_CEILING_Y = 0.13;

function liftClearOfGround(positions: number[], ground: DrawnGround, ceiling: number): void {
  let lift = 0;
  for (let i = 0; i < positions.length; i += 9) {
    // The centroid and the three edge midpoints: the interior low point of a draped
    // triangle is not at a vertex, and the vertices are exactly on the field by construction.
    for (const [px, pz, py] of [
      [(positions[i] + positions[i + 3] + positions[i + 6]) / 3, (positions[i + 2] + positions[i + 5] + positions[i + 8]) / 3, (positions[i + 1] + positions[i + 4] + positions[i + 7]) / 3],
      [(positions[i] + positions[i + 3]) / 2, (positions[i + 2] + positions[i + 5]) / 2, (positions[i + 1] + positions[i + 4]) / 2],
      [(positions[i + 3] + positions[i + 6]) / 2, (positions[i + 5] + positions[i + 8]) / 2, (positions[i + 4] + positions[i + 7]) / 2],
      [(positions[i + 6] + positions[i]) / 2, (positions[i + 8] + positions[i + 2]) / 2, (positions[i + 7] + positions[i + 1]) / 2],
    ]) {
      const sag = ground.heightAt(px, pz) + GROUND_CLEARANCE - py;
      if (sag > lift) lift = sag;
    }
  }
  if (lift <= 0) return;
  lift = Math.min(lift, ceiling);
  for (let i = 1; i < positions.length; i += 3) positions[i] += lift;
}

function refineTriangle(
  out: number[],
  ax: number,
  az: number,
  bx: number,
  bz: number,
  cx: number,
  cz: number,
  y: number,
  terrain: Heightfield,
  ground: DrawnGround,
  depth: number,
): void {
  // The longest edge that needs splitting, so that an edge which needs one always gets it
  // however the triangle around it is shaped.
  const ab = chordNeedsSplit(ax, az, bx, bz, terrain) || chordDipsIntoGround(ax, az, bx, bz, y, terrain, ground)
    ? (ax - bx) * (ax - bx) + (az - bz) * (az - bz)
    : -1;
  const bc = chordNeedsSplit(bx, bz, cx, cz, terrain) || chordDipsIntoGround(bx, bz, cx, cz, y, terrain, ground)
    ? (bx - cx) * (bx - cx) + (bz - cz) * (bz - cz)
    : -1;
  const ca = chordNeedsSplit(cx, cz, ax, az, terrain) || chordDipsIntoGround(cx, cz, ax, az, y, terrain, ground)
    ? (cx - ax) * (cx - ax) + (cz - az) * (cz - az)
    : -1;
  const longest = Math.max(ab, bc, ca);

  if (longest < 0 || depth === 0) {
    out.push(ax, y + terrain.heightAt(ax, az), az);
    out.push(bx, y + terrain.heightAt(bx, bz), bz);
    out.push(cx, y + terrain.heightAt(cx, cz), cz);
    return;
  }
  // Split it and recurse into both halves, keeping the winding.
  if (longest === ab) {
    const mx = (ax + bx) / 2;
    const mz = (az + bz) / 2;
    refineTriangle(out, ax, az, mx, mz, cx, cz, y, terrain, ground, depth - 1);
    refineTriangle(out, mx, mz, bx, bz, cx, cz, y, terrain, ground, depth - 1);
  } else if (longest === bc) {
    const mx = (bx + cx) / 2;
    const mz = (bz + cz) / 2;
    refineTriangle(out, ax, az, bx, bz, mx, mz, y, terrain, ground, depth - 1);
    refineTriangle(out, ax, az, mx, mz, cx, cz, y, terrain, ground, depth - 1);
  } else {
    const mx = (cx + ax) / 2;
    const mz = (cz + az) / 2;
    refineTriangle(out, ax, az, bx, bz, mx, mz, y, terrain, ground, depth - 1);
    refineTriangle(out, mx, mz, bx, bz, cx, cz, y, terrain, ground, depth - 1);
  }
}

/**
 * A fill, drawn the way its kind wants to be drawn.
 *
 * Water is the one kind that is not a coloured patch of ground: it is level, so it is drawn
 * as one flat plane at the height `waterLevels` chose for it rather than draped over the
 * terrain and lifted clear. That is cheaper — the Seine goes from 6138 refined triangles to
 * 99 — and it is the reason a bridge can be carried over the river at a known height instead
 * of racing a surface that moves under it.
 *
 * The map is empty on a flat world, so a region with no DEM falls through to exactly the
 * geometry it has always emitted. It also leaves out a stream that runs downhill, which has
 * no one level to be drawn at, and that is draped like a park.
 */
function addAreaPolygon(
  builder: MeshBuilder,
  area: Area,
  terrain: Heightfield,
  ground: DrawnGround | null,
  levels: Map<number, number>,
): void {
  const level = area.kind === 'water' ? levels.get(area.id) : undefined;
  if (level !== undefined) {
    addFlatPolygon(builder, area.poly, area.holes, level, area.color);
  } else if (terrain.isFlat || !ground) {
    addFlatPolygon(builder, area.poly, area.holes, area.y, area.color);
  } else {
    addDrapedPolygon(
      builder,
      area.poly,
      area.holes,
      area.y,
      area.color,
      terrain,
      ground,
      Math.max(0, FILL_CEILING_Y - area.y),
    );
  }
}

/**
 * A junction pad. Draped per vertex rather than at its centre: a disc is only ten or twenty
 * metres across, so following the ground across it costs nothing and stops a crossroads on a
 * hill standing proud of the carriageway on the uphill side.
 */
function addDisc(
  builder: MeshBuilder,
  cx: number,
  cz: number,
  y: number,
  radius: number,
  color: number,
  segments: number,
  terrain: Heightfield,
  surface?: (x: number, z: number) => number,
): void {
  if (radius <= 0.05) return;
  const at = surface ?? ((x: number, z: number) => terrain.heightAt(x, z));
  const positions: number[] = [cx, y + at(cx, cz), cz];
  const indices: number[] = [];
  for (let i = 0; i < segments; i++) {
    const a = (i / segments) * Math.PI * 2;
    const x = cx + Math.cos(a) * radius;
    const z = cz + Math.sin(a) * radius;
    positions.push(x, y + at(x, z), z);
  }
  for (let i = 0; i < segments; i++) {
    // Clockwise in (x,z) is counter-clockwise seen from +Y.
    indices.push(0, ((i + 1) % segments) + 1, i + 1);
  }
  builder.addIndexed(positions, indices, color, terrain.isFlat ? UP : undefined);
}

/**
 * The surface under each junction that is on a bridge, or on the ramp up to one, keyed by
 * its centre.
 *
 * A junction disc is drawn on the ground, and a junction on a viaduct is not on the ground:
 * the one in the middle of Wiadukt Grota-Roweckiego was a pad of tarmac six metres under
 * the crossroads it belonged to. The rim is lifted by whichever of the junction's own streets
 * runs nearest it, so a disc on a ramp tilts with the ramp rather than standing level out of
 * it. Junctions with nothing lifted through them are left out and keep the ground.
 */
function junctionSurfaces(
  world: World,
  decks: Map<number, DeckProfile>,
  lifts: Map<number, DeckProfile>,
  surfaceOf: (road: Road, x: number, z: number) => number,
): Map<string, (x: number, z: number) => number> {
  const through = new Map<string, Road[]>();
  for (const road of world.roads) {
    if (!decks.has(road.id) && !lifts.has(road.id)) continue;
    for (let i = 0; i < road.pts.length; i += 2) {
      const key = `${road.pts[i]}|${road.pts[i + 1]}`;
      const list = through.get(key);
      if (list) {
        if (!list.includes(road)) list.push(road);
      } else through.set(key, [road]);
    }
  }
  const out = new Map<string, (x: number, z: number) => number>();
  for (const j of world.junctions) {
    const roads = through.get(`${j.x}|${j.z}`);
    if (!roads) continue;
    out.set(`${j.x}|${j.z}`, (x, z) => {
      let best = roads[0];
      let bestD = Infinity;
      for (const road of roads) {
        for (let i = 2; i < road.pts.length; i += 2) {
          const d = distToSegment2(x, z, road.pts[i - 2], road.pts[i - 1], road.pts[i], road.pts[i + 1]).d2;
          if (d < bestD) {
            bestD = d;
            best = road;
          }
        }
      }
      return surfaceOf(best, x, z);
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// draping polylines
// ---------------------------------------------------------------------------

/**
 * Extra vertices along a centreline before it is turned into a ribbon.
 *
 * OSM leaves a straight as two points a hundred metres apart. Draped at its ends alone, the
 * ribbon between them is a chord: it cuts clean through anything in the way and floats over
 * the far side of it. Vertices go in wherever that chord departs from the ground, by the
 * same test the area fills use — which on a level grid of avenues is almost nowhere, and on
 * a street climbing out of a river valley is every few metres.
 *
 * This is a *densify*, not a resample, and the difference matters: `resample` walks by arc
 * length and would drop the original vertices, cutting every corner in the network by up to
 * half a step. The mesh has to keep the exact line the physics and the surface index are
 * built from — only with more points on it.
 */
function densifyToTerrain(pts: number[], terrain: Heightfield): number[] {
  const n = pts.length / 2;
  if (n < 2) return pts;
  const out: number[] = [pts[0], pts[1]];
  for (let i = 1; i < n; i++) {
    splitChord(out, pts[(i - 1) * 2], pts[(i - 1) * 2 + 1], pts[i * 2], pts[i * 2 + 1], terrain, REFINE_MAX_DEPTH);
  }
  return out;
}

/** Emits b, preceded by whatever midpoints the ground between a and b asks for. */
function splitChord(
  out: number[],
  ax: number,
  az: number,
  bx: number,
  bz: number,
  terrain: Heightfield,
  depth: number,
): void {
  if (depth > 0 && chordNeedsSplit(ax, az, bx, bz, terrain)) {
    const mx = (ax + bx) / 2;
    const mz = (az + bz) / 2;
    splitChord(out, ax, az, mx, mz, terrain, depth - 1);
    splitChord(out, mx, mz, bx, bz, terrain, depth - 1);
    return;
  }
  out.push(bx, bz);
}

/**
 * How a carriageway is graded, and how far it is allowed to get from the ground doing it.
 *
 * A road is built level across itself and eased along itself; the ground it crosses is
 * neither. Sampling the field once per vertex and using the answer raw gives a carriageway
 * that picks up every hummock left in the DEM. Averaging over a fixed twelve-metre window —
 * in *metres along the road*, not in vertices — takes those out; doing it per vertex would
 * not, because the vertices are spaced by what the terrain needs and a station whose
 * neighbours are forty metres away would be averaged against a different hill entirely.
 * That version of this smoothing pulled a Marais street four metres off the ground.
 *
 * The clamp is the part that matters. The car is placed by `heightAt` on the same field, so
 * anywhere the drawn road disagrees with it the wheels float above the tarmac or sink
 * through it — and the drawn ground is that field too, so a road that grades itself far
 * enough below it is simply buried. A kerb height is the whole budget for the disagreement.
 */
const GRADE_WINDOW = 6;
const GRADE_MAX_OFFSET = 0.15;

/**
 * The height a carriageway is drawn at over a point, given the direction it runs in.
 *
 * Exported because the road is not the only thing that has to sit on it. Anything painted
 * *onto* the tarmac — the route's guide line, its gates — has to agree with this and not
 * with the raw field, or it ends up under the road it is drawn on. Raw terrain is up to
 * `GRADE_MAX_OFFSET` below what is actually drawn, which is more than the clearance any of
 * those things can afford to leave.
 */
export function gradedRoadHeight(
  terrain: Heightfield,
  x: number,
  z: number,
  dirX: number,
  dirZ: number,
): number {
  const raw = terrain.heightAt(x, z);
  const len = Math.hypot(dirX, dirZ);
  if (terrain.isFlat || len < 1e-6) return raw;
  const tx = (dirX / len) * GRADE_WINDOW;
  const tz = (dirZ / len) * GRADE_WINDOW;
  const graded =
    (terrain.heightAt(x - tx, z - tz) + 2 * raw + terrain.heightAt(x + tx, z + tz)) / 4;
  return Math.min(raw + GRADE_MAX_OFFSET, Math.max(raw - GRADE_MAX_OFFSET, graded));
}

function drapeLine(
  pts: number[],
  terrain: Heightfield,
  deck: DeckProfile | null = null,
  lift: DeckProfile | null = null,
): { pts: number[]; h: Float64Array | null } {
  if (terrain.isFlat) return { pts, h: null };
  if (deck) {
    // No grading: a deck is carried, and there is nothing under it to follow. Densified to
    // the deck's own station spacing, because the ramps up to a lift are curves and OSM
    // leaves a bridge as two points two hundred metres apart — drawn between those alone,
    // the road would be a chord through the arch the car is driving over.
    const line = densifyEvenly(pts, DECK_DRAW_STEP);
    const h = new Float64Array(line.length / 2);
    for (let i = 0; i < h.length; i++) h[i] = deck(line[i * 2], line[i * 2 + 1]);
    return { pts: line, h };
  }
  const line = lift ? densifyEvenly(densifyToTerrain(pts, terrain), DECK_DRAW_STEP) : densifyToTerrain(pts, terrain);
  const n = line.length / 2;
  const h = new Float64Array(n);

  for (let i = 0; i < n; i++) {
    const x = line[i * 2];
    const z = line[i * 2 + 1];
    // Tangent from whichever neighbours exist, so the window follows the road rather than
    // an axis.
    const px = line[Math.max(0, i - 1) * 2];
    const pz = line[Math.max(0, i - 1) * 2 + 1];
    const nx = line[Math.min(n - 1, i + 1) * 2];
    const nz = line[Math.min(n - 1, i + 1) * 2 + 1];
    h[i] = gradedRoadHeight(terrain, x, z, nx - px, nz - pz) + (lift ? lift(x, z) : 0);
  }
  return { pts: line, h };
}

/** Same as `DeckIndex`'s station spacing, so the drawn ramp and the driven one agree. */
const DECK_DRAW_STEP = 4;

/** Extra points so no segment is longer than `step`, keeping every original vertex. */
function densifyEvenly(pts: number[], step: number): number[] {
  if (pts.length < 4) return pts;
  const out = [pts[0], pts[1]];
  for (let i = 2; i < pts.length; i += 2) {
    const ax = pts[i - 2];
    const az = pts[i - 1];
    const n = Math.max(1, Math.ceil(Math.hypot(pts[i] - ax, pts[i + 1] - az) / step));
    for (let k = 1; k <= n; k++) out.push(ax + ((pts[i] - ax) * k) / n, az + ((pts[i + 1] - az) * k) / n);
  }
  return out;
}

/**
 * Lifts a ribbon onto the draped centreline.
 *
 * Both edges of a station take the *centreline's* height, so the carriageway stays level
 * across its width. Sampling the field at each edge instead would bank the road on any
 * cross-slope, and a 20 m avenue on a hillside would arrive with a metre of camber it was
 * never built with.
 */
function drapeRibbon(positions: number[], h: Float64Array | null): void {
  if (!h) return;
  const stations = Math.min(h.length, positions.length / 6);
  for (let i = 0; i < stations; i++) {
    positions[i * 6 + 1] += h[i];
    positions[i * 6 + 4] += h[i];
  }
}

// ---------------------------------------------------------------------------
// roads
// ---------------------------------------------------------------------------

/**
 * A raised lip down both edges of the carriageway.
 *
 * Twelve triangles per road segment buys more apparent detail than anything else in this
 * file: it gives the tarmac a defined edge, it separates the road from whatever it runs
 * past, and at street level it is the thing that makes a road look like it was built rather
 * than painted on. Wherever another street's carriageway reaches the kerb line the kerb
 * stops, so a side street has a mouth exactly its own width and you never drive over a kerb
 * to turn left.
 *
 * That used to be the junction discs' job, and a disc is the wrong shape for it: it cut both
 * sides of the street for a disc's width around every node two ways share — including the far
 * side of a T, where nothing joins, and every node where OSM merely splits one street into
 * two ways, which it does at every change of tag. Each was a ten-metre gap in kerb and
 * pavement with bare ground in it (§93).
 */
function addKerbs(
  builder: MeshBuilder,
  road: Road,
  terrain: Heightfield,
  decks: Map<number, DeckProfile>,
  lifts: Map<number, DeckProfile>,
  carriageways: CarriagewayIndex,
  surfaceOf: (road: Road, x: number, z: number) => number,
): void {
  if (!ROAD_SPECS[road.cls].drivable || road.width < KERB_MIN_ROAD_WIDTH) return;
  const base = road.y;
  const top = base + KERB_HEIGHT;
  const deck = decks.get(road.id) ?? null;
  const lift = lifts.get(road.id) ?? null;
  // Per side, and none round a roundabout — see `pavementSides`.
  const pavementWidth = pavementSides(road);
  // Where the stone itself would stand: half a kerb out from the carriageway's edge.
  const reach = road.width / 2 + KERB_TOP_WIDTH / 2;
  // Any street at all, this one included, so a pavement on the inside of a hairpin does not
  // spread across its own carriageway.
  const paved = (x: number, z: number, y: number) => !carriageways.covers(x, z, y, -1);

  const run = road.pts;
  // Each side on its own, because a merging slip road takes the kerb off one side of an
  // avenue and leaves the other alone.
  for (const side of [0, 1] as const) {
    // `ribbon`'s convention: side 0 is the edge it emits first, at the tangent turned
    // a quarter towards -z.
    const sign = side === 0 ? -1 : 1;
    // The back of the pavement as well as the stone: where a street joins at an angle the
    // pavement's far corner reaches its carriageway first, and a segment whose pavement
    // would land on somebody's tarmac loses all of it (`paved` below), which on a long
    // segment was a long gap.
    const back = pavementWidth[side] > 0 ? road.width / 2 + KERB_TOP_WIDTH + pavementWidth[side] - 0.05 : 0;
    const pieces = clearRuns(run, (x, z, ux, uz) => {
      const y = surfaceOf(road, x, z);
      if (carriageways.covers(x - uz * reach * sign, z + ux * reach * sign, y, road.id)) return true;
      return back > 0 && carriageways.covers(x - uz * back * sign, z + ux * back * sign, y, road.id);
    });
    for (const piece of pieces) {
      const line = drapeLine(piece, terrain, deck, lift);
      const rib = ribbon(line.pts, road.width, base);
      const count = rib.positions.length / 6;
      for (let i = 1; i < count; i++) {
        const ga = line.h?.[i - 1] ?? 0;
        const gb = line.h?.[i] ?? 0;
        addKerbSegment(builder, rib.positions, i - 1, i, side, base, top, ga, gb, terrain, deck !== null, paved, pavementWidth[side]);
      }
    }
  }
}

/**
 * One side of one segment: the face the tyres see, the top of the stone, the pavement
 * outboard of it, and a skirt down the back of the pavement so there is no gap when you look
 * across the street from a low angle.
 *
 * `ga` and `gb` are the terrain heights at the two ends, both zero on a flat world — which
 * is how this stays byte-for-byte what it always emitted when there is no elevation data.
 */
function addKerbSegment(
  builder: MeshBuilder,
  positions: number[],
  i: number,
  j: number,
  side: 0 | 1,
  base: number,
  top: number,
  ga: number,
  gb: number,
  terrain: Heightfield,
  onDeck: boolean,
  paved: (x: number, z: number, y: number) => boolean,
  pavementWidth: number,
): void {
  // Every quad below is wound a → b, which faces up on side 0 and *down* on side 1, where
  // three culls it: for as long as there have been pavements, the one on the right of every
  // way's direction of travel — slab and kerb top — was invisible from above, and the
  // street showed whatever ground was under it (DECISIONS.md §93). So side 1 is walked the
  // other way, from j to i.
  if (side === 1) {
    [i, j] = [j, i];
    [ga, gb] = [gb, ga];
  }
  // The ribbon emits its two edges interleaved, so the pair straddling the centreline gives
  // the outward direction for free.
  const ax = positions[i * 6 + side * 3];
  const az = positions[i * 6 + side * 3 + 2];
  const bx = positions[j * 6 + side * 3];
  const bz = positions[j * 6 + side * 3 + 2];
  const oax = ax - positions[i * 6 + (1 - side) * 3];
  const oaz = az - positions[i * 6 + (1 - side) * 3 + 2];
  const len = Math.hypot(oax, oaz);
  if (len < 1e-4) return;
  const ox = oax / len;
  const oz = oaz / len;
  const tx = ox * KERB_TOP_WIDTH;
  const tz = oz * KERB_TOP_WIDTH;
  // The street runs across the outward direction.
  builder.axisX = -oz;
  builder.axisZ = ox;
  builder.surface = SURFACE_KERB;

  // Face toward the carriageway.
  builder.addQuad(
    ax, base + ga, az,
    bx, base + gb, bz,
    bx, top + gb, bz,
    ax, top + ga, az,
    -ox, 0, -oz,
    KERB_FACE_COLOR,
  );
  // The top of the stone.
  builder.addQuad(
    ax, top + ga, az,
    bx, top + gb, bz,
    bx + tx, top + gb, bz + tz,
    ax + tx, top + ga, az + tz,
    0, 1, 0,
    KERB_TOP_COLOR,
  );

  /*
   * The pavement, outboard of the stone and level with it — and the far edge the skirt
   * closes against.
   *
   * `tx,tz` is the offset to the outboard edge of the kerb stone; `outX,outZ` is where the
   * pavement (and the skirt beyond it) actually ends. They are the same offset unless the
   * pavement would land in another road's carriageway, which the kerb's own width says
   * nothing about — a slip road beside an avenue, or an oblique crossing that shares no OSM
   * node and so gets no junction disc to trim against. `placeLamps` in `props.ts` hit exactly
   * this in Manhattan. The test is at the pavement's own height, so the street under a
   * viaduct does not take the viaduct's pavement away. Falling back to `tx,tz` rather
   * than shrinking the pavement to fit is what keeps this a per-segment decision: a slab that
   * changed width every metre along a street would look like a modelling error, so a segment
   * either gets its pavement or, for the few that back onto another road, it doesn't.
   */
  const px = ox * pavementWidth;
  const pz = oz * pavementWidth;
  // Tested a few centimetres in from each end: a run cut where another street begins ends
  // within a bisection's precision of that street's edge, and the corner exactly on it must
  // not cost the whole segment its pavement.
  const inset = Math.min(0.5, 0.15 / Math.max(1e-3, Math.hypot(bx - ax, bz - az)));
  const hasPavement =
    pavementWidth > 0 &&
    paved(ax + (bx - ax) * inset + tx + px, az + (bz - az) * inset + tz + pz, ga) &&
    paved(bx - (bx - ax) * inset + tx + px, bz - (bz - az) * inset + tz + pz, gb);
  const outX = hasPavement ? tx + px : tx;
  const outZ = hasPavement ? tz + pz : tz;
  if (hasPavement) {
    builder.surface = SURFACE_PAVEMENT;
    builder.addQuad(
      ax + tx, top + ga, az + tz,
      bx + tx, top + gb, bz + tz,
      bx + outX, top + gb, bz + outZ,
      ax + outX, top + ga, az + outZ,
      0, 1, 0,
      PAVEMENT_COLOR,
    );
    builder.surface = SURFACE_KERB;
  }

  /*
   * Skirt down the back, buried in whatever the pavement (or, without one, the kerb) runs
   * past.
   *
   * On a slope it has further to go, and this is where it earns its keep. The carriageway is
   * drawn level across its width, so on a cross-fall the downhill kerb stands off the ground
   * — by up to 3 m on the streets running down to the Seine in the Marais fixture. Taking
   * the skirt to the ground under itself rather than to the level of the road closes that
   * gap for nothing: it is the same two triangles, just longer ones. A deck is exempt,
   * because the ground under a bridge is the riverbed and a skirt down to it would be a
   * seven-metre wall; `addElevatedStructure` gives a deck its edge instead. The ramp up to
   * one is not exempt, and there the skirt is the side of the embankment.
   *
   * On a deck it stops at the deck's own surface under the pavement (`DECK_FILL_Y`), where
   * the deck's edge takes over. It used to go on down to the deck's base, over the top of
   * that edge, and the two fought: the side of every bridge flickered between kerb and
   * concrete. That was half as bad until §93, because the right-hand skirt faced inwards
   * and was never drawn.
   */
  const skirtA = onDeck ? ga + base + DECK_FILL_Y : Math.min(ga, terrain.heightAt(ax + outX, az + outZ));
  const skirtB = onDeck ? gb + base + DECK_FILL_Y : Math.min(gb, terrain.heightAt(bx + outX, bz + outZ));
  builder.addQuad(
    ax + outX, top + ga, az + outZ,
    bx + outX, top + gb, bz + outZ,
    bx + outX, skirtB, bz + outZ,
    ax + outX, skirtA, az + outZ,
    ox, 0, oz,
    KERB_SIDE_COLOR,
  );
  builder.surface = SURFACE_TARMAC;
}

/**
 * What holds a lifted road up: the edge and underside of a bridge deck and the piers under
 * it, or the side of the embankment ramping up to one.
 *
 * A deck used to be a ribbon of tarmac and nothing else, which was fine while the only thing
 * that lifted one was a river crossing and the lift was a couple of metres over water. Once a
 * viaduct is six metres over the street it crosses, a ribbon reads as a sheet of paper in the
 * sky, and from the street underneath you could see straight up through the gap at its edge.
 *
 * Piers stand every `PIER_SPACING` metres where there is room for one and are left out where
 * they would stand in a street below. That makes the spacing irregular, which is what a real
 * viaduct over a town looks like anyway: the engineer had to miss the same streets.
 */
/** The deck's surface under its pavements, a hair below the carriageway it flanks. */
const DECK_FILL_Y = -0.005;
const DECK_FASCIA_COLOR = 0x9d988f;
const DECK_SOFFIT_COLOR = 0x5d5a55;
const PIER_COLOR = 0x8e8a82;
const PIER_SPACING = 30;
const PIER_THICKNESS = 1.4;
/** Clear height under a deck below which a pier would be a block rather than a column. */
const PIER_MIN_HEIGHT = 1.5;
/** A deck or ramp this close to the ground is lying on it, with no side to draw. */
const ON_THE_GROUND = 0.25;

function addElevatedStructure(
  builder: MeshBuilder,
  road: Road,
  terrain: Heightfield,
  decks: Map<number, DeckProfile>,
  lifts: Map<number, DeckProfile>,
  carriageways: CarriagewayIndex,
  onTrack: (x: number, z: number) => boolean = () => false,
): void {
  if (road.deck === 'tunnel') return;
  const deck = road.deck === 'bridge' ? decks.get(road.id) ?? null : null;
  const lift = deck ? null : lifts.get(road.id) ?? null;
  if (!deck && !lift) return;
  const kerbed = ROAD_SPECS[road.cls].drivable && road.width >= KERB_MIN_ROAD_WIDTH;
  // A kerbed ramp already has its embankment: the kerb's skirt runs down to the ground.
  if (lift && kerbed) return;

  const half = deck ? deckHalfWidth(road) : road.width / 2;
  const top = road.y + (kerbed ? KERB_HEIGHT : 0);
  const height = (x: number, z: number) => (deck ? deck(x, z) : terrain.heightAt(x, z) + lift!(x, z));

  for (const side of [0, 1] as const) {
    const sign = side === 0 ? -1 : 1;
    // Not across the mouth of a street joining on the deck, where the edge would stand as a
    // kerb-high ridge across the carriageway.
    const pieces = clearRuns(road.pts, (x, z, ux, uz) =>
      carriageways.covers(x - uz * half * sign, z + ux * half * sign, height(x, z), road.id),
    );
    for (const piece of pieces) {
      const line = drapeLine(piece, terrain, deck, lift);
      if (!line.h) continue;
      const rib = ribbon(line.pts, half * 2, 0);
      const carriageway = ribbon(line.pts, road.width, 0);
      const count = Math.min(line.h.length, rib.positions.length / 6);
      for (let i = 1; i < count; i++) {
        const a = edgeOf(rib.positions, i - 1, side);
        const b = edgeOf(rib.positions, i, side);
        const ha = line.h[i - 1];
        const hb = line.h[i];
        if (deck) {
          // The deck between the carriageway and its edge, a hair under the pavement that
          // normally covers it. Where there is no pavement — trimmed at a junction, or kept
          // off a neighbouring carriageway — this is what stops the ground showing through a
          // slot down the side of the bridge.
          const ia = edgeOf(carriageway.positions, i - 1, side);
          const ib = edgeOf(carriageway.positions, i, side);
          const y = road.y + DECK_FILL_Y;
          addQuadFacing(builder, [ia.x, ha + y, ia.z], [ib.x, hb + y, ib.z], [b.x, hb + y, b.z], [a.x, ha + y, a.z], [0, 1, 0], DECK_FASCIA_COLOR);
        }
        const ga = terrain.heightAt(a.x, a.z);
        const gb = terrain.heightAt(b.x, b.z);
        if (ha - ga < ON_THE_GROUND && hb - gb < ON_THE_GROUND) continue;
        const bottomA = deck ? ha - DECK_DEPTH : Math.min(ga, ha);
        const bottomB = deck ? hb - DECK_DEPTH : Math.min(gb, hb);
        // Up to the kerb on a kerbed deck is the kerb's own skirt; see `addKerbSegment`.
        const edgeTop = deck && kerbed ? road.y + DECK_FILL_Y : top;
        addQuadFacing(
          builder,
          [a.x, ha + edgeTop, a.z],
          [b.x, hb + edgeTop, b.z],
          [b.x, bottomB, b.z],
          [a.x, bottomA, a.z],
          [a.ox, 0, a.oz],
          DECK_FASCIA_COLOR,
        );
      }
    }
  }
  if (!deck) return;

  const line = drapeLine(road.pts, terrain, deck);
  if (!line.h) return;
  const rib = ribbon(line.pts, half * 2, 0);
  const count = Math.min(line.h.length, rib.positions.length / 6);
  for (let i = 1; i < count; i++) {
    const ha = line.h[i - 1] - DECK_DEPTH;
    const hb = line.h[i] - DECK_DEPTH;
    const a0 = edgeOf(rib.positions, i - 1, 0);
    const a1 = edgeOf(rib.positions, i - 1, 1);
    const b0 = edgeOf(rib.positions, i, 0);
    const b1 = edgeOf(rib.positions, i, 1);
    if (ha < terrain.heightAt(a0.x, a0.z) && hb < terrain.heightAt(b0.x, b0.z)) continue;
    addQuadFacing(builder, [a0.x, ha, a0.z], [a1.x, ha, a1.z], [b1.x, hb, b1.z], [b0.x, hb, b0.z], [0, -1, 0], DECK_SOFFIT_COLOR);
  }

  const length = polylineLength(line.pts);
  const reach = half * 0.7;
  for (let d = PIER_SPACING / 2; d < length; d += PIER_SPACING) {
    const p = pointAlong(line.pts, d);
    const underside = deck(p.x, p.z) - DECK_DEPTH;
    const ground = terrain.heightAt(p.x, p.z);
    if (underside - ground < PIER_MIN_HEIGHT) continue;
    const feet = [
      [p.x, p.z],
      [p.x - p.dz * reach, p.z + p.dx * reach],
      [p.x + p.dz * reach, p.z - p.dx * reach],
    ];
    if (feet.some(([x, z]) => carriageways.covers(x, z, terrain.heightAt(x, z), road.id) || onTrack(x, z))) continue;
    addPier(builder, p.x, p.z, p.dx, p.dz, ground - 0.3, underside, PIER_THICKNESS / 2, reach);
  }
}

const PARAPET_COLOR = 0xb1aca3;

/**
 * The wall along a deck edge, from `DeckIndex.parapets` — the same line the physics stops a
 * car at, so what you see is what you hit.
 *
 * It stands on the pavement and its outer face is the fascia carried up, so from the street
 * the deck edge simply reads a metre deeper; from the deck it is a low concrete wall.
 */
function addParapet(builder: MeshBuilder, parapet: Parapet, road: Road): void {
  const kerbed = ROAD_SPECS[road.cls].drivable && road.width >= KERB_MIN_ROAD_WIDTH;
  const foot = road.y + (kerbed ? KERB_HEIGHT : 0);
  const { outer, inner, y } = parapet;
  const n = y.length;
  for (let k = 1; k < n; k++) {
    const o0 = [outer[(k - 1) * 2], outer[(k - 1) * 2 + 1]];
    const o1 = [outer[k * 2], outer[k * 2 + 1]];
    const i0 = [inner[(k - 1) * 2], inner[(k - 1) * 2 + 1]];
    const i1 = [inner[k * 2], inner[k * 2 + 1]];
    const out = [(o0[0] + o1[0] - i0[0] - i1[0]) / 2, 0, (o0[1] + o1[1] - i0[1] - i1[1]) / 2];
    const lo0 = y[k - 1] + foot;
    const lo1 = y[k] + foot;
    const hi0 = y[k - 1] + PARAPET_HEIGHT;
    const hi1 = y[k] + PARAPET_HEIGHT;
    addQuadFacing(builder, [o0[0], lo0, o0[1]], [o1[0], lo1, o1[1]], [o1[0], hi1, o1[1]], [o0[0], hi0, o0[1]], out, PARAPET_COLOR);
    addQuadFacing(builder, [i0[0], lo0, i0[1]], [i1[0], lo1, i1[1]], [i1[0], hi1, i1[1]], [i0[0], hi0, i0[1]], [-out[0], 0, -out[2]], PARAPET_COLOR);
    addQuadFacing(builder, [o0[0], hi0, o0[1]], [o1[0], hi1, o1[1]], [i1[0], hi1, i1[1]], [i0[0], hi0, i0[1]], [0, 1, 0], PARAPET_COLOR);
  }
  // The two ends, where the wall stops at a junction mouth or where the deck comes down low
  // enough not to need it.
  for (const [k, toward] of [[0, 1], [n - 1, n - 2]] as const) {
    const o = [outer[k * 2], outer[k * 2 + 1]];
    const i = [inner[k * 2], inner[k * 2 + 1]];
    const face = [outer[k * 2] - outer[toward * 2], 0, outer[k * 2 + 1] - outer[toward * 2 + 1]];
    addQuadFacing(builder, [o[0], y[k] + foot, o[1]], [i[0], y[k] + foot, i[1]], [i[0], y[k] + PARAPET_HEIGHT, i[1]], [o[0], y[k] + PARAPET_HEIGHT, o[1]], face, PARAPET_COLOR);
  }
}

/** One edge of a ribbon at a station, and the outward direction there. */
function edgeOf(positions: number[], i: number, side: 0 | 1): { x: number; z: number; ox: number; oz: number } {
  const x = positions[i * 6 + side * 3];
  const z = positions[i * 6 + side * 3 + 2];
  const ox = x - positions[i * 6 + (1 - side) * 3];
  const oz = z - positions[i * 6 + (1 - side) * 3 + 2];
  const len = Math.hypot(ox, oz) || 1;
  return { x, z, ox: ox / len, oz: oz / len };
}

/** A box standing on end, long side across the deck it holds up. */
function addPier(
  builder: MeshBuilder,
  x: number,
  z: number,
  ux: number,
  uz: number,
  bottom: number,
  top: number,
  halfAlong: number,
  halfAcross: number,
): void {
  const corner = (along: number, across: number): [number, number] => [
    x + ux * along - uz * across,
    z + uz * along + ux * across,
  ];
  const ring = [corner(-halfAlong, -halfAcross), corner(halfAlong, -halfAcross), corner(halfAlong, halfAcross), corner(-halfAlong, halfAcross)];
  for (let k = 0; k < 4; k++) {
    const [ax, az] = ring[k];
    const [bx, bz] = ring[(k + 1) % 4];
    const mx = (ax + bx) / 2 - x;
    const mz = (az + bz) / 2 - z;
    addQuadFacing(builder, [ax, top, az], [bx, top, bz], [bx, bottom, bz], [ax, bottom, az], [mx, 0, mz], PIER_COLOR);
  }
}

/**
 * A quad wound to face `normal`, whichever order its corners came in. Everything in this
 * file is single-sided, and a wall wound the wrong way is a wall that is not there.
 */
function addQuadFacing(
  builder: MeshBuilder,
  a: number[],
  b: number[],
  c: number[],
  d: number[],
  normal: number[],
  color: number,
): void {
  const len = Math.hypot(normal[0], normal[1], normal[2]) || 1;
  const [nx, ny, nz] = [normal[0] / len, normal[1] / len, normal[2] / len];
  const e1 = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
  const e2 = [c[0] - a[0], c[1] - a[1], c[2] - a[2]];
  const cross = [e1[1] * e2[2] - e1[2] * e2[1], e1[2] * e2[0] - e1[0] * e2[2], e1[0] * e2[1] - e1[1] * e2[0]];
  const facing = cross[0] * nx + cross[1] * ny + cross[2] * nz >= 0;
  const [p, q, r, t] = facing ? [a, b, c, d] : [a, d, c, b];
  builder.addQuad(p[0], p[1], p[2], q[0], q[1], q[2], r[0], r[1], r[2], t[0], t[1], t[2], nx, ny, nz, color);
}

/**
 * A transparent decal of lane paint over the wider roads.
 *
 * `ribbon` already emits `u` across the carriageway and `v` along it, which is exactly the
 * parameterisation lane markings want — the only adjustment is rescaling `v` from the
 * ribbon's units (multiples of the road width) into a fixed distance on the ground, so the
 * dashes have the same rhythm on a 10 m street as on a 22 m avenue.
 */
function addLaneMarkings(
  builder: MeshBuilder,
  road: Road,
  junctions: JunctionIndex,
  terrain: Heightfield,
  decks: Map<number, DeckProfile>,
  lifts: Map<number, DeckProfile>,
  carriageways: CarriagewayIndex,
  surfaceOf: (road: Road, x: number, z: number) => number,
): void {
  if (!ROAD_SPECS[road.cls].drivable || road.width < MARKING_MIN_ROAD_WIDTH) return;
  const vScale = road.width / MARKING_PERIOD_METRES;
  const deck = decks.get(road.id) ?? null;
  const lift = lifts.get(road.id) ?? null;
  // By the centreline only. Where the two halves of a dual carriageway merge, each one's
  // centre line runs across the other's tarmac and has to go; further apart, their ribbons
  // still overlap at the edges, and testing the edges would strip the paint off both.
  const runs = junctions
    .clear(road.pts, 1.6, 8)
    .flatMap((run) => clearRuns(run, (x, z) => carriageways.covers(x, z, surfaceOf(road, x, z), road.id), 8));

  for (const run of runs) {
    const line = drapeLine(run, terrain, deck, lift);
    const rib = ribbon(line.pts, road.width, road.y + 0.014);
    drapeRibbon(rib.positions, line.h);
    const uvs = new Array<number>(rib.uvs.length);
    for (let i = 0; i < rib.uvs.length; i += 2) {
      uvs[i] = rib.uvs[i];
      uvs[i + 1] = rib.uvs[i + 1] * vScale;
    }
    // The decal has to be draped with the same heights as the tarmac under it, or the paint
    // sinks into the road on one side of a crest and lifts off it on the other.
    builder.addIndexed(rib.positions, rib.indices, MARKING_COLOR, line.h ? undefined : UP, uvs);
  }
}

/**
 * Zebra crossings at the mouths of the junctions.
 *
 * Derived, not mapped. OSM does carry real crossings, as `highway=crossing` nodes, and they
 * would be better — but the query asks for ways and for tagged POI nodes only, so adding
 * them means changing the Overpass request *and* re-baking every committed fixture, which is
 * a network round trip this cannot make on its own. What the payload already has is where
 * the junctions are and which streets meet there, which puts a crossing at the one place a
 * crossing reliably is.
 *
 * Only junctions where three or more streets meet, and only on arms wide enough to have been
 * given lane markings in the first place: a crossing painted across a 5 m service alley is
 * the same mistake as a centre line down one.
 *
 * And only where one fits. Every node where a street meets a roundabout is a three-way
 * junction, so the first version of this striped Rondo Zatorze all the way round: across the
 * ring itself at every entry, and across entries a few metres long, on top of the crossings
 * of the next junction along. The rules below are what a highway engineer would have said:
 * never across the ring, set back from it, along the street rather than along a straight line
 * from the node, and nowhere the paint would land on another street or another crossing.
 */
const CROSSING_MIN_ARMS = 3;
/** Metres of tarmac between the edge of the junction disc and the near edge of the paint. */
const CROSSING_SETBACK = 1.4;
/**
 * The same, at the entry to a roundabout: a car length, so the car waiting to join stands
 * behind the crossing rather than on it.
 */
const ROUNDABOUT_SETBACK = 5;
/** How far along the street the paint runs. A real crossing is about this deep. */
const CROSSING_LENGTH = 4;
/** Clear tarmac a crossing needs between itself and the next junction down its street. */
const CROSSING_CLEARANCE = 1;

interface PaintedCrossing {
  x: number;
  z: number;
  ux: number;
  uz: number;
  halfLength: number;
  halfWidth: number;
}

function addCrossings(
  builder: MeshBuilder,
  nodes: JunctionNode[],
  terrain: Heightfield,
  decks: Map<number, DeckProfile>,
  lifts: Map<number, DeckProfile>,
  carriageways: CarriagewayIndex,
  surfaceOf: (road: Road, x: number, z: number) => number,
): void {
  const byKey = new Map<string, JunctionNode>();
  for (const node of nodes) byKey.set(`${node.x}|${node.z}`, node);
  // Only the real junctions: a disc where a street merely changes way is a fillet on a bend,
  // and a crossing a few metres from one is still at the mouth of its own junction.
  const junctions = new JunctionIndex(
    nodes.filter((n) => n.arms.length >= CROSSING_MIN_ARMS).map((n) => ({ x: n.x, z: n.z, r: n.r, color: 0, y: 0 })),
  );
  const painted: PaintedCrossing[] = [];
  const half = CROSSING_LENGTH / 2;

  for (const node of nodes) {
    if (node.arms.length < CROSSING_MIN_ARMS) continue;
    const onRing = node.arms.some((arm) => arm.roundabout);
    for (const arm of node.arms) {
      if (arm.width < MARKING_MIN_ROAD_WIDTH || arm.roundabout) continue;
      const road = arm.road;

      // Centre of the paint, measured out along the street from the middle of the junction.
      const along = node.r + (onRing ? ROUNDABOUT_SETBACK : CROSSING_SETBACK) + half;
      const street = walkArm(arm, byKey, along + half + CROSSING_CLEARANCE);
      if (!street) continue;
      const centre = pointAlong(street.pts, along);
      const ux = centre.dx;
      const uz = centre.dz;
      const crossing: PaintedCrossing = { x: centre.x, z: centre.z, ux, uz, halfLength: half, halfWidth: arm.width / 2 };

      const y = surfaceOf(road, centre.x, centre.z);
      const a = [centre.x - ux * half, centre.z - uz * half];
      const b = [centre.x + ux * half, centre.z + uz * half];
      // Into another junction's disc, onto another street, or over a crossing already
      // painted — any one of them means this is not a mouth of a junction but the middle of
      // something else.
      const clear = junctions.clear([...a, ...b], 0.5, 0);
      if (clear.length !== 1 || Math.abs(polylineLength(clear[0]) - CROSSING_LENGTH) > 1e-3) continue;
      const inset = Math.max(0, arm.width / 2 - 0.3);
      const corners = [
        [a[0] - uz * inset, a[1] + ux * inset],
        [a[0] + uz * inset, a[1] - ux * inset],
        [b[0] - uz * inset, b[1] + ux * inset],
        [b[0] + uz * inset, b[1] - ux * inset],
      ];
      if (corners.some(([x, z]) => carriageways.covers(x, z, y, street.roads))) continue;
      if (painted.some((other) => rectanglesOverlap(crossing, other))) continue;
      painted.push(crossing);

      const line = drapeLine([...a, ...b], terrain, decks.get(arm.roadId) ?? null, lifts.get(arm.roadId) ?? null);
      // A hair higher than the lane paint, so where a crossing meets a stop line the zebra
      // is the one on top rather than whichever the depth test happened to prefer.
      const rib = ribbon(line.pts, arm.width, road.y + 0.018);
      drapeRibbon(rib.positions, line.h);
      // `ribbon` measures `v` in multiples of the road width; the crossing texture wants
      // exactly 0..1 over its own length, and it is clamped, so this has to be rescaled or
      // a wide street would show one stripe stretched over the whole crossing. The deck
      // densifies the line, so `v` is taken from the ribbon's own running length.
      const vScale = arm.width / CROSSING_LENGTH;
      const uvs = new Array<number>(rib.uvs.length);
      for (let i = 0; i < rib.uvs.length; i += 2) {
        uvs[i] = rib.uvs[i];
        uvs[i + 1] = rib.uvs[i + 1] * vScale;
      }
      builder.addIndexed(rib.positions, rib.indices, MARKING_COLOR, line.h ? undefined : UP, uvs);
    }
  }
}

/**
 * The street an arm leads down, as a polyline from the junction, for at least `need` metres
 * — or null if the next real junction, or the end of the street, comes first.
 *
 * Through a node where the street merely changes way, because OSM splits a street wherever
 * its tags change and a crossing does not care that the lane count did.
 */
function walkArm(
  arm: JunctionNode['arms'][number],
  byKey: Map<string, JunctionNode>,
  need: number,
): { pts: number[]; roads: Set<number> } | null {
  const pts: number[] = [];
  const roads = new Set<number>();
  let road = arm.road;
  let i = arm.at;
  let step = arm.step;
  let length = 0;
  pts.push(road.pts[i * 2], road.pts[i * 2 + 1]);
  roads.add(road.id);
  for (let guard = 0; guard < 64; guard++) {
    const k = i + step;
    if (k < 0 || k >= road.pts.length / 2) return null;
    const x = road.pts[k * 2];
    const z = road.pts[k * 2 + 1];
    length += Math.hypot(x - pts[pts.length - 2], z - pts[pts.length - 1]);
    pts.push(x, z);
    if (length >= need) return { pts, roads };
    const next = byKey.get(`${x}|${z}`);
    if (next) {
      if (next.arms.length !== 2) return null;
      // A bend between two ways of the same street: carry on down the other one.
      const onward = next.arms.find((a) => !(a.road === road && a.at === k && a.step === -step));
      if (!onward) return null;
      road = onward.road;
      i = onward.at;
      step = onward.step;
      roads.add(road.id);
      continue;
    }
    i = k;
  }
  return null;
}

/** Separating axis test for two crossings, each a rectangle along its own street. */
function rectanglesOverlap(a: PaintedCrossing, b: PaintedCrossing): boolean {
  const axes = [
    [a.ux, a.uz],
    [-a.uz, a.ux],
    [b.ux, b.uz],
    [-b.uz, b.ux],
  ];
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  for (const [ax, az] of axes) {
    const reach = (r: PaintedCrossing) =>
      Math.abs(r.ux * ax + r.uz * az) * r.halfLength + Math.abs(-r.uz * ax + r.ux * az) * r.halfWidth;
    if (Math.abs(dx * ax + dz * az) > reach(a) + reach(b)) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// buildings
// ---------------------------------------------------------------------------

/**
 * The ground-floor band, in metres of wall.
 *
 * Four metres is a storey and a bit: a real shopfront is taller than the flats above it,
 * because it has a fascia and an awning over a full-height window. Anything much shorter
 * stops reading as a shopfront from a car and just looks like a plinth.
 *
 * Buildings shorter than `BAND_MIN_HEIGHT` get no band at all. On a 5 m lock-up the band
 * would be most of the elevation, which turns "a shop on the ground floor" into "a shop
 * painted the wrong colour".
 */
export const BAND_METRES = 4;
export const BAND_MIN_HEIGHT = 7;

/**
 * A hipped roof, and the limits on where one is worth attempting.
 *
 * The pitch scales with the *narrow* side of the footprint, because that is what sets the
 * angle a roof reads at: a 9 m terrace and a 40 m school both want something around 25°,
 * and driving the height off the long side would put a 10 m spike on the school. The cap is
 * what stops a long footprint growing a circus tent — past about 3.5 m the fan to a single
 * apex stops looking like a roof and starts looking like a marquee.
 */
const PITCH_RISE_PER_METRE = 0.28;
const PITCH_MAX_HEIGHT = 3.5;
const PITCH_MIN_SPAN = 3;
/** Above this the footprint is a warehouse or a shopping centre, and those roofs are flat. */
const PITCH_MAX_AREA = 900;
/**
 * Past this many corners the ring is a traced curve rather than a plan, and the fan becomes
 * a lot of thin slivers. Sixteen rather than a tighter number because a round tower is a
 * traced curve too, and a cone on top of one is exactly right — at twelve, a quarter of
 * Kraków's churches fell back to a flat cap.
 */
const PITCH_MAX_VERTICES = 16;

/** Rooftop machinery: a plant room and a chiller, in metres. */
const PLANT_MIN_SPAN = 12;
const PLANT_COLOR = 0x8b8f94;
const PLANT_TOP_COLOR = 0x9aa0a6;

/**
 * A stable number in [0,1) for (seed, building id, draw index) — the same trick `props.ts`
 * uses. Folding the index into the seed rather than into the id keeps each building's
 * sequence independent of every other building's, which is the whole point: adding one
 * office block must not move the air handling units on the one next door.
 */
function draw(seed: number, id: number, k: number): number {
  return hashUnit(seed ^ Math.imul(k + 1, 0x9e3779b1), id);
}

/** Zero on a flat world, so a building's Y is exactly what it was before terrain existed. */
function lowestGroundUnder(poly: number[], terrain: Heightfield): number {
  if (terrain.isFlat) return 0;
  let lowest = Infinity;
  for (let i = 0; i < poly.length; i += 2) {
    const h = terrain.heightAt(poly[i], poly[i + 1]);
    if (h < lowest) lowest = h;
  }
  return Number.isFinite(lowest) ? lowest : 0;
}

/**
 * Extrudes a footprint: a roof plus one quad per outer edge. Holes are triangulated into
 * the roof but get no walls — a courtyard's inner walls are never visible from a car that
 * cannot get into the courtyard.
 *
 * Roof and walls go into separate builders because the walls carry a facade texture and the
 * roofs must not: a roof is seen from directly above, where a window grid would be nonsense.
 *
 * `lift` is the height of the terrain under the footprint; every Y here is measured from it.
 *
 * Colour and roof style arrive in the payload from `classify.ts` rather than being decided
 * here. The roof palette that used to live in this file was deliberately warm, and it has
 * to stay warm wherever it lives: the first version was mostly slate greys, and from a few
 * hundred metres up the roofs and the tarmac were the same colour — which cost the player
 * the one thing they need to be able to see, which is where the road goes.
 */
function addBuilding(
  roofs: MeshBuilder,
  walls: MeshBuilder,
  b: Building,
  seed: number,
  lift: number,
  covered?: Map<number, [number, number][]>,
): void {
  const top = b.height + lift;
  const bottom = b.minHeight + lift;
  if (!(b.roofStyle === 'pitched' && addPitchedRoof(roofs, b, top, b.roof))) {
    addFlatPolygon(roofs, b.poly, b.holes, top, b.roof);
    if (b.roofStyle === 'plant') addRoofPlant(roofs, b, top, seed);
  }

  // Walls are shaded a little darker than the roof used to be, and the shader darkens them
  // further toward the pavement.
  const wallColor = shade(b.color, 0.94);
  // A band is two extra triangles per edge, so it is only emitted for the buildings that
  // have one — about a third of a city centre, and none of the suburbs.
  const bandColor = b.base ?? null;
  const banded = bandColor !== null && top - bottom >= BAND_MIN_HEIGHT;
  const bandTop = bottom + BAND_METRES;
  const storeys = Math.max(1, Math.round((top - (banded ? bandTop : bottom)) / FLOOR_METRES));

  const look = facadeSeedFor(seed, b.id);
  const bayMetres = bayMetresFor(b.style, look);
  const facade: WallFacade = {
    style: Math.max(0, FACADE_STYLES.indexOf(b.style)) + (banded ? 8 * FACADE_FLAG_BANDED : 0),
    material: Math.max(0, FACADE_MATERIALS.indexOf(b.material)),
    seed: look,
    bays: 1,
    bottom,
    top,
    bayMetres,
    storeyMetres: (top - (banded ? bandTop : bottom)) / storeys,
  };
  const bandFacade: WallFacade = {
    ...facade,
    style: facade.style + 8 * FACADE_FLAG_BAND,
    storeyMetres: BAND_METRES,
  };

  const n = b.poly.length / 2;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const ax = b.poly[i * 2];
    const az = b.poly[i * 2 + 1];
    const bx = b.poly[j * 2];
    const bz = b.poly[j * 2 + 1];
    const dx = bx - ax;
    const dz = bz - az;
    const len = Math.hypot(dx, dz);
    if (len < 1e-3) continue;
    // Outward normal for a positive-area ring.
    const nx = dz / len;
    const nz = -dx / len;
    // Whole bays only, so a window is never sliced in half at the corner of a building.
    const bays = Math.max(1, Math.round(len / bayMetres));
    facade.bays = bays;
    bandFacade.bays = bays;
    // Where another building's wall already stands here, this one is left out (see
    // `sharedWalls.ts`). What is left keeps its place in the bays of the whole edge, so
    // the windows on either side of a gap still line up with the corners.
    for (const [from, to] of uncovered(len, covered?.get(i))) {
      const px = ax + (dx * from) / len;
      const pz = az + (dz * from) / len;
      const qx = ax + (dx * to) / len;
      const qz = az + (dz * to) / len;
      const u0 = (bays * from) / len;
      const u1 = (bays * to) / len;
      if (banded) {
        // The band is one storey of its own, 4 m tall: a shopfront is taller than the floors
        // over it, and a fractional V range would cut a window off at the fascia.
        walls.addWallQuad(px, pz, qx, qz, bottom, bandTop, nx, nz, bandColor!, bays, 1, bandFacade, u0, u1);
        walls.addWallQuad(px, pz, qx, qz, bandTop, top, nx, nz, wallColor, bays, storeys, facade, u0, u1);
      } else {
        walls.addWallQuad(px, pz, qx, qz, bottom, top, nx, nz, wallColor, bays, storeys, facade, u0, u1);
      }
    }
  }
}

/**
 * A hip roof: the footprint ring fanned up to a raised centroid.
 *
 * Two triangles more than the flat cap it replaces, and it is the single clearest signal in
 * the aerial view of what a building *is* — a district of houses stops looking like a
 * district of offices without a colour change or a texture.
 *
 * Returns false when the footprint is the wrong shape for a fan, and the caller falls back
 * to a flat roof. The star-shape test is the one that matters: a fan is only valid if the
 * centroid can see every edge, which an L-plan or a courtyard block cannot promise. Testing
 * that directly rather than testing convexity keeps every rotated rectangle in OSM — which
 * is most of them — out of the fallback.
 */
function addPitchedRoof(builder: MeshBuilder, b: Building, y: number, color: number): boolean {
  const poly = b.poly;
  const n = poly.length / 2;
  if (b.holes.length > 0 || n < 3 || n > PITCH_MAX_VERTICES) return false;
  if (Math.abs(polyArea(poly)) > PITCH_MAX_AREA) return false;

  const bb = bboxOfPoly(poly);
  const span = Math.min(bb.maxX - bb.minX, bb.maxZ - bb.minZ);
  if (span < PITCH_MIN_SPAN) return false;

  const c = polyCentroid(poly);
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    // Positive cross product means the centroid is on the inward side of this edge.
    const cross =
      (poly[j * 2] - poly[i * 2]) * (c.z - poly[i * 2 + 1]) -
      (poly[j * 2 + 1] - poly[i * 2 + 1]) * (c.x - poly[i * 2]);
    if (cross <= 0) return false;
  }

  const rise = Math.min(PITCH_MAX_HEIGHT, span * PITCH_RISE_PER_METRE);
  const positions: number[] = [];
  for (let i = 0; i < n; i++) positions.push(poly[i * 2], y, poly[i * 2 + 1]);
  positions.push(c.x, y + rise, c.z);

  const indices: number[] = [];
  for (let i = 0; i < n; i++) {
    // Apex in the middle of the triple: with a positive-area ring that is the winding whose
    // normal points up and out over the eaves.
    indices.push(i, n, (i + 1) % n);
  }
  // No shared normal: each slope of a hip roof catches the sun differently, and that is
  // most of what makes it read as a roof rather than as a coloured cap.
  builder.addIndexed(positions, indices, color);
  return true;
}

/**
 * Two boxes of rooftop machinery on an office or a shed.
 *
 * Twenty triangles buys the thing a flat roof is missing from above, which is any evidence
 * that it is a building rather than a swatch. Positions, sizes and heights all come from
 * `hashUnit` keyed on the building id, never from a sequential stream, so the plant on one
 * roof is not disturbed by whether the roof next to it exists.
 */
function addRoofPlant(builder: MeshBuilder, b: Building, y: number, seed: number): void {
  const bb = bboxOfPoly(b.poly);
  const spanX = bb.maxX - bb.minX;
  const spanZ = bb.maxZ - bb.minZ;
  if (Math.min(spanX, spanZ) < PLANT_MIN_SPAN) return;
  const c = polyCentroid(b.poly);

  for (let k = 0; k < 2; k++) {
    const u = draw(seed, b.id, k * 2);
    const v = draw(seed, b.id, k * 2 + 1);
    // Kept well inside the footprint: plant that overhangs the parapet looks like a
    // modelling error, and clipping it against the ring properly is not worth 20 triangles.
    const px = c.x + (u - 0.5) * spanX * 0.42;
    const pz = c.z + (v - 0.5) * spanZ * 0.42;
    if (!pointInPoly(b.poly, px, pz)) continue;
    let inHole = false;
    for (const hole of b.holes) if (pointInPoly(hole, px, pz)) inHole = true;
    if (inHole) continue;

    const hw = (1.6 + u * 1.7) / 2;
    const hd = (1.5 + v * 1.5) / 2;
    const h = 1.1 + ((u * 7.31) % 1) * 1.5;
    addBox(builder, px, y, pz, hw, h, hd);
  }
}

/** Four sides and a lid. The underside is on the roof, so it is never emitted. */
function addBox(
  builder: MeshBuilder,
  x: number,
  y: number,
  z: number,
  hw: number,
  h: number,
  hd: number,
): void {
  const x0 = x - hw;
  const x1 = x + hw;
  const z0 = z - hd;
  const z1 = z + hd;
  const y1 = y + h;
  builder.addQuad(x1, y, z0, x0, y, z0, x0, y1, z0, x1, y1, z0, 0, 0, -1, PLANT_COLOR);
  builder.addQuad(x0, y, z1, x1, y, z1, x1, y1, z1, x0, y1, z1, 0, 0, 1, PLANT_COLOR);
  builder.addQuad(x1, y, z1, x1, y, z0, x1, y1, z0, x1, y1, z1, 1, 0, 0, PLANT_COLOR);
  builder.addQuad(x0, y, z0, x0, y, z1, x0, y1, z1, x0, y1, z0, -1, 0, 0, PLANT_COLOR);
  builder.addQuad(x0, y1, z1, x1, y1, z1, x1, y1, z0, x0, y1, z0, 0, 1, 0, PLANT_TOP_COLOR);
}

// ---------------------------------------------------------------------------
// MeshBuilder
// ---------------------------------------------------------------------------

const _tmpA = new THREE.Vector3();
const _tmpB = new THREE.Vector3();
const _tmpC = new THREE.Vector3();
const _tmpN = new THREE.Vector3();
const _color = new THREE.Color();
const _uv = [0, 0, 0, 0, 0, 0];

/** What the facade shader is told about the wall a quad belongs to. See `facade.ts`. */
interface WallFacade {
  /** Index into `FACADE_STYLES`, plus 8 × the `FACADE_FLAG_*` bits. */
  style: number;
  /** Index into `FACADE_MATERIALS`. */
  material: number;
  /** 0–1, per building: everything the shader varies is hashed from it. */
  seed: number;
  /** How many bays this wall has, so the shader can find its corners. */
  bays: number;
  /** The building's base and top, world Y, whichever quad of it this is. */
  bottom: number;
  top: number;
  bayMetres: number;
  storeyMetres: number;
}

interface MeshOptions {
  flatShading: boolean;
  /**
   * Every surface in the city is a dielectric with no metal in it; this is how rough. It
   * mostly decides how much sky a surface reflects at a grazing angle — a road at 0.86
   * picks up a sheen down the street ahead, a lawn at 0.92 picks up nothing.
   */
  roughness: number;
  map?: THREE.Texture;
  /** Added at zero intensity; see `setNight`. */
  emissiveMap?: THREE.Texture;
  transparent?: boolean;
}

/**
 * Accumulates non-indexed triangles with per-vertex colour and normal, then hands back a
 * single BufferGeometry. Non-indexed costs a little memory but means every element can
 * carry its own flat-shaded normal without any vertex splitting logic.
 *
 * Texture coordinates are opt-in: only two of the six merged meshes are textured, and
 * carrying a dead UV attribute across half a million vertices is 4 MB of nothing.
 */
class MeshBuilder {
  private positions: number[] = [];
  private normals: number[] = [];
  private colors: number[] = [];
  private uvs: number[] | null;
  /** The facade shader's two attributes per vertex; only the wall builder asks for them. */
  private facadeA: number[] | null;
  private facadeB: number[] | null;
  /** What subsequent triangles are tagged with. Set by `addWallQuad`. */
  private facade: WallFacade | null = null;
  /**
   * The street surface and its direction per vertex, for the road shader (`road.ts`): which
   * of tarmac, pavement or kerb stone a triangle is, and which way the street runs there, so
   * a pavement's slabs line up with it. Only the road builder asks for them.
   */
  private surfaces: number[] | null;
  private axes: number[] | null;
  surface: number = SURFACE_TARMAC;
  axisX = 1;
  axisZ = 0;

  constructor(opts: { uv?: boolean; facade?: boolean; surface?: boolean } = {}) {
    this.uvs = opts.uv ? [] : null;
    this.facadeA = opts.facade ? [] : null;
    this.facadeB = opts.facade ? [] : null;
    this.surfaces = opts.surface ? [] : null;
    this.axes = opts.surface ? [] : null;
  }

  get triangleCount(): number {
    return this.positions.length / 9;
  }

  /** Appends an indexed mesh, expanding it to individual triangles. */
  addIndexed(
    positions: number[],
    indices: number[],
    color: number,
    normal?: THREE.Vector3,
    uvs?: number[],
  ): void {
    _color.setHex(color, THREE.SRGBColorSpace);
    for (let i = 0; i < indices.length; i += 3) {
      const ia = indices[i] * 3;
      const ib = indices[i + 1] * 3;
      const ic = indices[i + 2] * 3;
      _tmpA.set(positions[ia], positions[ia + 1], positions[ia + 2]);
      _tmpB.set(positions[ib], positions[ib + 1], positions[ib + 2]);
      _tmpC.set(positions[ic], positions[ic + 1], positions[ic + 2]);
      if (normal) {
        _tmpN.copy(normal);
      } else {
        _tmpN.subVectors(_tmpB, _tmpA).cross(_tmpC.clone().sub(_tmpA)).normalize();
      }
      if (uvs) {
        const ua = indices[i] * 2;
        const ub = indices[i + 1] * 2;
        const uc = indices[i + 2] * 2;
        _uv[0] = uvs[ua];
        _uv[1] = uvs[ua + 1];
        _uv[2] = uvs[ub];
        _uv[3] = uvs[ub + 1];
        _uv[4] = uvs[uc];
        _uv[5] = uvs[uc + 1];
      }
      this.pushTri(_tmpA, _tmpB, _tmpC, _tmpN, uvs ? _uv : null);
    }
  }

  addQuad(
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number,
    cx: number, cy: number, cz: number,
    dx: number, dy: number, dz: number,
    nx: number, ny: number, nz: number,
    color: number,
  ): void {
    _color.setHex(color, THREE.SRGBColorSpace);
    _tmpN.set(nx, ny, nz);
    _tmpA.set(ax, ay, az);
    _tmpB.set(bx, by, bz);
    _tmpC.set(cx, cy, cz);
    this.pushTri(_tmpA, _tmpB, _tmpC, _tmpN, null);
    _tmpB.set(cx, cy, cz);
    _tmpC.set(dx, dy, dz);
    this.pushTri(_tmpA, _tmpB, _tmpC, _tmpN, null);
  }

  /**
   * One face of a building, mapped in bays across and storeys up, so the facade lands on
   * whole windows and whole floors however long or tall the wall happens to be.
   *
   * `facade` is everything the facade shader needs to know about the building — see
   * `facade.ts`. It is carried per vertex rather than per draw because the whole city is one
   * mesh: a uniform would mean one draw call per building, and a merged mesh has nowhere
   * else to put it.
   *
   * `u0`..`u1` is the part of the edge's `bays` this quad covers, when it is only a piece of
   * the edge.
   */
  addWallQuad(
    ax: number, az: number,
    bx: number, bz: number,
    bottom: number, top: number,
    nx: number, nz: number,
    color: number,
    bays: number,
    storeys: number,
    facade: WallFacade | null = null,
    u0 = 0,
    u1 = bays,
  ): void {
    _color.setHex(color, THREE.SRGBColorSpace);
    _tmpN.set(nx, 0, nz);
    this.facade = facade;

    _tmpA.set(ax, bottom, az);
    _tmpB.set(ax, top, az);
    _tmpC.set(bx, top, bz);
    _uv[0] = u0; _uv[1] = 0;
    _uv[2] = u0; _uv[3] = storeys;
    _uv[4] = u1; _uv[5] = storeys;
    this.pushTri(_tmpA, _tmpB, _tmpC, _tmpN, _uv);

    _tmpB.set(bx, top, bz);
    _tmpC.set(bx, bottom, bz);
    _uv[0] = u0; _uv[1] = 0;
    _uv[2] = u1; _uv[3] = storeys;
    _uv[4] = u1; _uv[5] = 0;
    this.pushTri(_tmpA, _tmpB, _tmpC, _tmpN, _uv);
  }

  private pushTri(
    a: THREE.Vector3,
    b: THREE.Vector3,
    c: THREE.Vector3,
    n: THREE.Vector3,
    uv: number[] | null,
  ): void {
    this.positions.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);
    for (let i = 0; i < 3; i++) {
      this.normals.push(n.x, n.y, n.z);
      this.colors.push(_color.r, _color.g, _color.b);
    }
    if (this.uvs) {
      if (uv) this.uvs.push(uv[0], uv[1], uv[2], uv[3], uv[4], uv[5]);
      else this.uvs.push(0, 0, 0, 0, 0, 0);
    }
    if (this.surfaces && this.axes) {
      for (let i = 0; i < 3; i++) {
        this.surfaces.push(this.surface);
        this.axes.push(this.axisX, this.axisZ);
      }
    }
    if (this.facadeA && this.facadeB) {
      const f = this.facade;
      for (let i = 0; i < 3; i++) {
        if (f) {
          this.facadeA.push(f.style, f.material, f.seed, f.bays);
          this.facadeB.push(f.bottom, f.top, f.bayMetres, f.storeyMetres);
        } else {
          this.facadeA.push(0, 0, 0, 1);
          this.facadeB.push(0, 0, BAY_METRES, FLOOR_METRES);
        }
      }
    }
  }

  toMesh(
    name: string,
    opts: MeshOptions,
  ): { mesh: THREE.Mesh; geometry: THREE.BufferGeometry; material: THREE.Material; triangles: number } | null {
    if (this.positions.length === 0) return null;
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(this.positions, 3));
    geometry.setAttribute('normal', new THREE.Float32BufferAttribute(this.normals, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(this.colors, 3));
    if (this.uvs) geometry.setAttribute('uv', new THREE.Float32BufferAttribute(this.uvs, 2));
    if (this.surfaces && this.axes) {
      geometry.setAttribute('surface', new THREE.Float32BufferAttribute(this.surfaces, 1));
      geometry.setAttribute('streetAxis', new THREE.Float32BufferAttribute(this.axes, 2));
    }
    if (this.facadeA && this.facadeB) {
      geometry.setAttribute('facadeA', new THREE.Float32BufferAttribute(this.facadeA, 4));
      geometry.setAttribute('facadeB', new THREE.Float32BufferAttribute(this.facadeB, 4));
    }
    geometry.computeBoundingSphere();
    const material = new THREE.MeshStandardMaterial({
      vertexColors: true,
      flatShading: opts.flatShading,
      roughness: opts.roughness,
      metalness: 0,
      // Spread rather than `map: opts.map`: Three.js walks the parameter object by key and
      // warns about every one whose value is undefined, so passing the key at all costs a
      // console warning per untextured mesh.
      ...(opts.map ? { map: opts.map } : {}),
      // Emissive starts fully off, so a daytime scene renders exactly what it always did.
      // The map is attached at build time rather than when night falls: `emissiveMap` is
      // what sets the shader define, so assigning it later means a recompile — a visible
      // hitch — at exactly the wrong moment. Leaving it attached costs one texture fetch
      // per wall fragment, which is nothing beside what this shader already does.
      ...(opts.emissiveMap
        ? { emissiveMap: opts.emissiveMap, emissive: 0xffffff, emissiveIntensity: 0 }
        : {}),
      transparent: opts.transparent ?? false,
      // A decal sits a centimetre over opaque tarmac; writing depth from it only gives the
      // route furniture something to sort against.
      depthWrite: !opts.transparent,
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = name;
    mesh.matrixAutoUpdate = false;
    return { mesh, geometry, material, triangles: this.positions.length / 9 };
  }
}
